import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useApp } from '../context/AppContext';
import GameMapBackground from '../components/GameMapBackground';
import '../styles/Pages.css';
import '../styles/Admin.css';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
} from 'recharts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SERVER = API.replace(/\/api$/, '');
const authHeaders = () => {
  const token = sessionStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Resolve a stored photo path to a displayable URL
const resolvePhoto = (photoUrl) => {
  if (!photoUrl) return null;
  if (photoUrl.startsWith('http')) return photoUrl; // legacy external URL
  return `${SERVER}${photoUrl}`;
};

const fmtPhone = (raw) => {
  const digits = (raw || '').replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return raw;
};

// Normalize a timestamp to a Date. SQLite's CURRENT_TIMESTAMP is UTC in
// "YYYY-MM-DD HH:MM:SS" (no zone), which JS would otherwise misread as local time.
// ISO strings (with T/Z or offset) pass straight through.
const parseTs = (v) => {
  if (!v) return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(v)) {
    return new Date(v.replace(' ', 'T') + 'Z');
  }
  return new Date(v);
};

const fmtDate = (v) => {
  const d = parseTs(v);
  return d ? d.toLocaleDateString('en-US', { timeZone: 'America/New_York' }) : '—';
};

const fmtDateTime = (v) => {
  const d = parseTs(v);
  return d ? d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
};

const SMS_FILTER_OPTIONS = [
  { value: 'all',           label: 'All Guests' },
  { value: 'not-invited',   label: 'Not Yet Invited' },
  { value: 'no-response',   label: 'No Response' },
  { value: 'attending-all', label: 'All Attending (Playing + Dinner/Cheer)' },
  { value: 'playing',       label: 'Playing only' },
  { value: 'attending',     label: 'Dinner / Cheerleading only' },
  { value: 'unavailable',   label: 'Unavailable' },
  { value: 'test',          label: 'Test (only me)' },
];
const filterLabel = (v) => (SMS_FILTER_OPTIONS.find(o => o.value === v) || {}).label || v;

// Extract plain text from an exceljs cell value (which may be a string, number,
// hyperlink object {text, hyperlink}, rich text {richText}, or formula {result})
const cellText = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    if (typeof v.text === 'string') return v.text;
    if (Array.isArray(v.richText)) return v.richText.map(t => t.text).join('');
    if (v.result !== undefined) return String(v.result);
    if (v.hyperlink) return v.hyperlink;
    return '';
  }
  return String(v);
};

// Minimal CSV line parser (handles quoted fields with embedded commas/quotes)
const parseCsvLine = (line) => {
  const out = [];
  let cur = '', inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = false;
      } else cur += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
};

// datetime-local <-> ISO helpers for scheduled messages
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localInputToIso = (local) => local ? new Date(local).toISOString() : '';

// Parse pasted rules text into { intro, sections:[{title, items:[]}] }.
// Bullet lines (-, *, •, –, ·, ▪, or "1." / "1)") become items; other lines become section titles.
// A leading sentence (ends with . ! ?) before any section is treated as the intro.
const parseGameRules = (text) => {
  const isBullet = (l) => /^([-*•–·▪]|\d+[.)])\s+/.test(l);
  const stripBullet = (l) => l.replace(/^([-*•–·▪]|\d+[.)])\s+/, '').trim();
  const sections = [];
  let intro = '';
  let current = null;
  (text || '').split(/\r?\n/).forEach(raw => {
    const line = raw.trim();
    if (!line) return;
    if (isBullet(line)) {
      if (!current) { current = { title: 'Rules', items: [] }; sections.push(current); }
      current.items.push(stripBullet(line));
    } else if (!current && sections.length === 0 && /[.!?]$/.test(line) && !line.endsWith(':')) {
      intro = intro ? `${intro} ${line}` : line;
    } else {
      current = { title: line.replace(/:$/, '').trim(), items: [] };
      sections.push(current);
    }
  });
  return { intro, sections };
};

const AdminPage = () => {
  const { invites, rsvps, addInvite, addInvitesBulk, updateInvite, toggleInviteSent, deleteInvite, updateRSVP, settings, updateSettings, clearGuestList, loadFullInvites, loadData } = useApp();

  // ── Auth state ──────────────────────────────────────────────────────────────
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem('admin_token'));
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: loginPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setLoginError(data.error || 'Login failed.');
      } else {
        sessionStorage.setItem('admin_token', data.token);
        setAuthed(true);
        loadFullInvites();
      }
    } catch {
      setLoginError('Could not reach server. Try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignOut = () => {
    sessionStorage.removeItem('admin_token');
    setAuthed(false);
    setLoginPassword('');
    loadData();
  };
  // ────────────────────────────────────────────────────────────────────────────

  const [newInvite, setNewInvite] = useState({ name: '', email: '', phone: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoFileRef = useRef(null);
  const [showPhotoMenu, setShowPhotoMenu] = useState(null);
  const [photoMenuPos, setPhotoMenuPos] = useState({ top: 0, left: 0 });
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterContact, setFilterContact] = useState('all');
  const [activeView, setActiveView] = useState('guests'); // guests | statistics | bringing | notes
  const [contactPopup, setContactPopup] = useState(null);
  const [notesSearch, setNotesSearch] = useState('');
  const [notesSort, setNotesSort] = useState('name');
  const [bringingSearch, setBringingSearch] = useState('');
  const [editingRSVP, setEditingRSVP] = useState(null);
  const [importPreview, setImportPreview] = useState(null); // { toAdd: [], duplicates: [] }
  const [excludedImport, setExcludedImport] = useState(() => new Set()); // indices into toAdd to skip
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [adminSection, setAdminSection] = useState('guests'); // guests | communicate | event
  const [videosDraft, setVideosDraft] = useState(null); // null = not yet initialized
  const [videosSaving, setVideosSaving] = useState(false);
  const [videosSaved, setVideosSaved] = useState(false);
  const [newVideoForm, setNewVideoForm] = useState({ title: '', year: '', youtubeUrl: '' });
  const [markingAllSent, setMarkingAllSent] = useState(false);
  const [newScheduleForm, setNewScheduleForm] = useState({ time: '', description: '' });
  const [newHighlightForm, setNewHighlightForm] = useState('');
  // Gallery
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  // Games admin
  const [gamesAdminData, setGamesAdminData] = useState([]);
  const [gamesAdminLoading, setGamesAdminLoading] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [gameDraft, setGameDraft] = useState(null);
  const [gameSaving, setGameSaving] = useState(false);
  const [gameAddMode, setGameAddMode] = useState(false);
  const [gameAddShape, setGameAddShape] = useState('rect');
  const [gameLineStep, setGameLineStep] = useState(null);
  const [gameNewSection, setGameNewSection] = useState({ title: '', item: '' });
  const [gameRulesPaste, setGameRulesPaste] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);
  const adminMapRef = useRef(null);
  const gameDragRef = useRef(null);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [smsFilter, setSmsFilter] = useState('all');
  const [smsMessage, setSmsMessage] = useState('');
  const [smsMarkInviteSent, setSmsMarkInviteSent] = useState(false);
  const [smsIncludeRsvpLink, setSmsIncludeRsvpLink] = useState(false);
  const [smsIncludeVideoLink, setSmsIncludeVideoLink] = useState(false);
  const [smsOverrides, setSmsOverrides] = useState({}); // inviteId -> 'include' | 'exclude'
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsSendResult, setSmsSendResult] = useState(null);
  const [smsLog, setSmsLog] = useState([]);
  const [smsLogLoading, setSmsLogLoading] = useState(false);
  // Scheduled messages
  const [scheduledMsgs, setScheduledMsgs] = useState([]);
  const [schedLoading, setSchedLoading] = useState(false);
  const [schedDraft, setSchedDraft] = useState(null); // null = no form open
  const [schedSaving, setSchedSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Load full invite data (with PII) if already authenticated on mount
  React.useEffect(() => {
    if (authed) loadFullInvites();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close photo menu, contact popup, and emoji picker on outside click
  React.useEffect(() => {
    const handler = (e) => {
      setShowPhotoMenu(null);
      setContactPopup(null);
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Fetch unread message count on mount
  React.useEffect(() => {
    fetch(`${API}/messages/unread-count`)
      .then(r => r.json())
      .then(d => setUnreadCount(d.count || 0))
      .catch(() => {});
  }, []);

  const fetchMessages = () => {
    setMessagesLoading(true);
    fetch(`${API}/messages`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        setMessages(data);
        setUnreadCount(data.filter(m => !m.isRead).length);
      })
      .catch(() => {})
      .finally(() => setMessagesLoading(false));
  };

  const markAllRead = () => {
    fetch(`${API}/messages/read-all`, { method: 'PUT', headers: authHeaders() })
      .then(() => {
        setMessages(prev => prev.map(m => ({ ...m, isRead: true })));
        setUnreadCount(0);
      })
      .catch(() => {});
  };

  const deleteMessage = (id) => {
    const msg = messages.find(m => m.id === id);
    fetch(`${API}/messages/${id}`, { method: 'DELETE', headers: authHeaders() })
      .then(() => {
        setMessages(prev => prev.filter(m => m.id !== id));
        if (msg && !msg.isRead) setUnreadCount(prev => Math.max(0, prev - 1));
      })
      .catch(() => {});
  };

  // ── Settings modal ───────────────────────────────────────
  const openSettings = () => {
    const pad = n => String(n).padStart(2, '0');
    const toLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setSettingsDraft({
      event_date:        toLocal(settings.eventDate),
      event_end_date:    toLocal(settings.eventEndDate),
      rsvp_deadline:     toLocal(settings.rsvpDeadline),
      enforce_deadline:  settings.enforceDeadline,
      event_description: settings.eventDescription || '',
      event_location:    settings.eventLocation || '',
      attire:            settings.attire || '',
      schedule:          settings.schedule   || [],
      highlights:        settings.highlights || [],
    });
    setSettingsOpen(true);
  };

  // ── SMS ───────────────────────────────────────────────────
  const getSmsRecipients = (filter = smsFilter) => {
    if (filter === 'test') {
      return { withPhone: [{ id: 0, name: 'Test (admin)', phone: '+17169080639' }], skipped: [] };
    }
    let filtered;
    if (filter === 'all')          filtered = invites;
    else if (filter === 'not-invited') filtered = invites.filter(i => !i.inviteSent);
    else if (filter === 'no-response') filtered = invites.filter(i => !rsvps.find(r => r.inviteeId === i.id));
    else if (filter === 'attending-all') filtered = invites.filter(i => {
      const r = rsvps.find(rv => rv.inviteeId === i.id);
      return r && (r.status === 'playing' || r.status === 'attending');
    });
    else filtered = invites.filter(i => {
      const r = rsvps.find(rv => rv.inviteeId === i.id);
      return r && r.status === filter;
    });
    return {
      withPhone: filtered.filter(i => i.phone && i.phone.trim()),
      skipped:   filtered.filter(i => !i.phone || !i.phone.trim()),
    };
  };

  // Override-aware recipient resolution: filter group ± manual per-person overrides.
  // Returns per-invite rows plus the final included/skipped counts.
  const getFinalRecipientInfo = (filter = smsFilter) => {
    if (filter === 'test') {
      return { rows: [], includedCount: 1, skippedNoPhone: 0, isTest: true };
    }
    const { withPhone, skipped } = getSmsRecipients(filter);
    const inFilterIds = new Set([...withPhone, ...skipped].map(i => i.id));
    const rows = invites.map(inv => {
      const hasPhone = !!(inv.phone && inv.phone.trim());
      const override = smsOverrides[inv.id]; // 'include' | 'exclude' | undefined
      const inFilter = inFilterIds.has(inv.id);
      let included;
      if (override === 'include')      included = hasPhone;
      else if (override === 'exclude') included = false;
      else                             included = inFilter && hasPhone;
      return { inv, hasPhone, inFilter, override, included };
    });
    return {
      rows,
      includedCount: rows.filter(r => r.included).length,
      skippedNoPhone: rows.filter(r => r.inFilter && !r.hasPhone && r.override !== 'exclude').length,
      isTest: false,
    };
  };

  const overrideCount = Object.keys(smsOverrides).length;
  const featuredVideo = (settings.videos || []).find(v => v.id === settings.featuredVideoId) || (settings.videos || [])[0] || null;

  const fetchSmsLog = () => {
    setSmsLogLoading(true);
    fetch(`${API}/sms/log`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setSmsLog(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setSmsLogLoading(false));
  };

  const handleSmsSend = async () => {
    const info = getFinalRecipientInfo();
    if (!smsMessage.trim() || info.includedCount === 0) return;
    setSmsSending(true);
    setSmsSendResult(null);
    const includeIds = Object.entries(smsOverrides).filter(([, v]) => v === 'include').map(([id]) => Number(id));
    const excludeIds = Object.entries(smsOverrides).filter(([, v]) => v === 'exclude').map(([id]) => Number(id));
    try {
      const res = await fetch(`${API}/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          message: smsMessage, filter: smsFilter,
          markInviteSent: smsMarkInviteSent, includeRsvpLink: smsIncludeRsvpLink,
          includeVideoLink: smsIncludeVideoLink,
          ...(smsFilter !== 'test' ? { includeIds, excludeIds } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');
      setSmsSendResult({ ...data, ok: true });
      setSmsMessage('');
      setSmsOverrides({});
      fetchSmsLog();
    } catch (err) {
      setSmsSendResult({ error: err.message });
    } finally {
      setSmsSending(false);
    }
  };

  // ── Scheduled messages ────────────────────────────────────
  const fetchScheduled = () => {
    setSchedLoading(true);
    fetch(`${API}/scheduled-messages`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setScheduledMsgs(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setSchedLoading(false));
  };

  const newSchedDraft = () => ({ id: null, label: '', message: '', filter: 'no-response', includeRsvpLink: true, includeVideoLink: false, markInviteSent: false, sendAtLocal: '', enabled: true });

  const editSchedDraft = (m) => setSchedDraft({
    id: m.id, label: m.label, message: m.message, filter: m.filter,
    includeRsvpLink: m.includeRsvpLink, includeVideoLink: m.includeVideoLink, markInviteSent: m.markInviteSent,
    sendAtLocal: isoToLocalInput(m.sendAt), enabled: m.enabled,
  });

  const saveSchedDraft = async () => {
    if (!schedDraft.message.trim() || !schedDraft.sendAtLocal) return;
    setSchedSaving(true);
    const payload = {
      label: schedDraft.label,
      message: schedDraft.message,
      filter: schedDraft.filter,
      includeRsvpLink: schedDraft.includeRsvpLink,
      includeVideoLink: schedDraft.includeVideoLink,
      markInviteSent: schedDraft.markInviteSent,
      sendAt: localInputToIso(schedDraft.sendAtLocal),
      enabled: schedDraft.enabled,
    };
    try {
      const url = schedDraft.id ? `${API}/scheduled-messages/${schedDraft.id}` : `${API}/scheduled-messages`;
      const res = await fetch(url, {
        method: schedDraft.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setSchedDraft(null);
      fetchScheduled();
    } catch {
      alert('Failed to save scheduled message.');
    } finally {
      setSchedSaving(false);
    }
  };

  const deleteScheduled = async (id) => {
    await fetch(`${API}/scheduled-messages/${id}`, { method: 'DELETE', headers: authHeaders() });
    setScheduledMsgs(prev => prev.filter(m => m.id !== id));
  };

  const toggleScheduledEnabled = async (m) => {
    const res = await fetch(`${API}/scheduled-messages/${m.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ enabled: !m.enabled }),
    });
    if (res.ok) {
      const updated = await res.json();
      setScheduledMsgs(prev => prev.map(x => x.id === updated.id ? updated : x));
    }
  };

  // ── Games Admin ───────────────────────────────────────────
  const fetchGamesAdmin = useCallback(() => {
    setGamesAdminLoading(true);
    fetch(`${API}/games/all`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setGamesAdminData(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setGamesAdminLoading(false));
  }, []);

  const setGameArchived = async (gameId, archived) => {
    const res = await fetch(`${API}/games/${gameId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ archived }),
    });
    if (!res.ok) return;
    const updated = await res.json();
    setGamesAdminData(prev => prev.map(g => g.id === updated.id ? updated : g));
    if (archived && selectedGameId === gameId) { setSelectedGameId(null); setGameDraft(null); }
  };

  const getAdminSvgCoords = (e) => {
    const svg = adminMapRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: Math.round((e.clientX - rect.left) * 460 / rect.width),
      y: Math.round((e.clientY - rect.top) * 560 / rect.height),
    };
  };

  const selectGame = (game) => {
    setSelectedGameId(game.id);
    setGameDraft({ ...game, rules: game.rules.map(s => ({ ...s, items: [...s.items] })) });
    setGameAddMode(false);
    setGameLineStep(null);
  };

  const saveGamePosition = async (gameId, shapeData) => {
    await fetch(`${API}/games/${gameId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ shapeData }),
    });
  };

  const saveGameDraft = async () => {
    if (!gameDraft) return;
    setGameSaving(true);
    try {
      const res = await fetch(`${API}/games/${gameDraft.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          name: gameDraft.name, emoji: gameDraft.emoji, color: gameDraft.color,
          shape: gameDraft.shape, shapeData: gameDraft.shapeData,
          indoor: gameDraft.indoor, intro: gameDraft.intro, rules: gameDraft.rules,
        }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setGamesAdminData(prev => prev.map(g => g.id === updated.id ? updated : g));
      setGameDraft({ ...updated, rules: updated.rules.map(s => ({ ...s, items: [...s.items] })) });
    } catch { alert('Failed to save. Please try again.'); }
    finally { setGameSaving(false); }
  };

  const deleteGameAdmin = async (gameId) => {
    await fetch(`${API}/games/${gameId}`, { method: 'DELETE', headers: authHeaders() });
    setGamesAdminData(prev => prev.filter(g => g.id !== gameId));
    setSelectedGameId(null);
    setGameDraft(null);
  };

  const createGameFromClick = async (shapeData, shape) => {
    const res = await fetch(`${API}/games`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ name: 'New Game', emoji: '🎮', color: '#667eea', shape, shapeData, indoor: false, intro: '', rules: [] }),
    });
    if (!res.ok) return;
    const created = await res.json();
    setGamesAdminData(prev => [...prev, created]);
    selectGame(created);
  };

  const handleAdminMapClick = (e) => {
    if (!gameAddMode) return;
    const { x, y } = getAdminSvgCoords(e);
    if (gameAddShape === 'line') {
      if (!gameLineStep) {
        setGameLineStep({ x, y });
      } else {
        createGameFromClick({ x1: gameLineStep.x, y1: gameLineStep.y, x2: x, y2: y }, 'line');
        setGameLineStep(null);
        setGameAddMode(false);
      }
    } else if (gameAddShape === 'rect') {
      createGameFromClick({ x: x - 30, y: y - 20, w: 60, h: 40 }, 'rect');
      setGameAddMode(false);
    } else {
      createGameFromClick({ cx: x, cy: y, r: 25 }, 'circle');
      setGameAddMode(false);
    }
  };

  const handleGameDragStart = (e, game) => {
    if (gameAddMode) return;
    e.stopPropagation();
    const { x, y } = getAdminSvgCoords(e);
    gameDragRef.current = { gameId: game.id, startX: x, startY: y, origShapeData: { ...game.shapeData } };
    if (selectedGameId !== game.id) selectGame(game);
  };

  const handleAdminMapMouseMove = (e) => {
    if (!gameDragRef.current) return;
    const { x, y } = getAdminSvgCoords(e);
    const dx = x - gameDragRef.current.startX;
    const dy = y - gameDragRef.current.startY;
    const d = gameDragRef.current.origShapeData;
    const gameId = gameDragRef.current.gameId;
    const game = gamesAdminData.find(g => g.id === gameId);
    let newSD;
    if (game.shape === 'rect')   newSD = { ...d, x: d.x + dx, y: d.y + dy };
    if (game.shape === 'circle') newSD = { ...d, cx: d.cx + dx, cy: d.cy + dy };
    if (game.shape === 'line')   newSD = { x1: d.x1+dx, y1: d.y1+dy, x2: d.x2+dx, y2: d.y2+dy };
    setGamesAdminData(prev => prev.map(g => g.id === gameId ? { ...g, shapeData: newSD } : g));
    setGameDraft(prev => prev?.id === gameId ? { ...prev, shapeData: newSD } : prev);
  };

  const handleAdminMapMouseUp = () => {
    if (!gameDragRef.current) return;
    const gameId = gameDragRef.current.gameId;
    gameDragRef.current = null;
    const game = gamesAdminData.find(g => g.id === gameId);
    if (game) saveGamePosition(gameId, game.shapeData);
  };

  // ── Gallery ───────────────────────────────────────────────
  const fetchGallery = () => {
    setGalleryLoading(true);
    fetch(`${API}/photos`)
      .then(r => r.json())
      .then(data => setGalleryPhotos(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setGalleryLoading(false));
  };

  const handleGalleryDelete = async (photoId) => {
    await fetch(`${API}/photos/${photoId}`, { method: 'DELETE', headers: authHeaders() });
    setGalleryPhotos(prev => prev.filter(p => p.id !== photoId));
  };


  // ── helpers ──────────────────────────────────────────────
  const getRSVP = (inviteId) => rsvps.find(r => r.inviteeId === inviteId);

  const getStatus = (inviteId) => {
    const r = getRSVP(inviteId);
    if (!r) return 'no-response';
    return r.status || 'no-response';
  };

  const statusLabel = (s) => ({
    playing: 'Playing',
    attending: 'Attending',
    unavailable: 'Unavailable',
    'no-response': 'No Response',
  }[s] || s);

  const getLastName = (fullName) => {
    const parts = fullName.trim().split(' ');
    return parts[parts.length - 1];
  };

  // ── stats ─────────────────────────────────────────────────
  const resolveStatus = (r) => r.status || 'no-response';

  const totalInvites = invites.length;
  const invitesSent = invites.filter(i => i.inviteSent).length;
  const responded = rsvps.length;
  const noResponse = totalInvites - responded;
  const playingCount = rsvps.filter(r => resolveStatus(r) === 'playing').length;
  const attendingCount = rsvps.filter(r => resolveStatus(r) === 'attending').length;
  const unavailableCount = rsvps.filter(r => resolveStatus(r) === 'unavailable').length;
  const usaCount = rsvps.filter(r => r.team === 'USA').length;
  const canadaCount = rsvps.filter(r => r.team === 'Canada').length;
  const cheerleaderCount = rsvps.filter(r => r.team === 'Cheerleader').length;
  const notesCount = rsvps.filter(r => r.additionalNotes && r.additionalNotes.trim()).length;
  const bringingCount = rsvps.filter(r => r.bringingItem && r.bringingItem.trim()).length;

  // ── filtered invite list ──────────────────────────────────
  const getFilteredInvites = () => {
    let filtered = [...invites];
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter(i =>
        i.name.toLowerCase().includes(s) ||
        i.email.toLowerCase().includes(s) ||
        i.phone.toLowerCase().includes(s)
      );
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter(i => getStatus(i.id) === filterStatus);
    }
    if (filterContact === 'has-email') {
      filtered = filtered.filter(i => i.email && i.email.trim());
    } else if (filterContact === 'has-phone') {
      filtered = filtered.filter(i => i.phone && i.phone.trim());
    } else if (filterContact === 'missing-contact') {
      filtered = filtered.filter(i => (!i.email || !i.email.trim()) && (!i.phone || !i.phone.trim()));
    }
    filtered.sort((a, b) => getLastName(a.name).localeCompare(getLastName(b.name)));
    return filtered;
  };

  // ── notes ─────────────────────────────────────────────────
  const getFilteredNotes = () => {
    let notes = rsvps
      .filter(r => r.additionalNotes && r.additionalNotes.trim())
      .map(r => {
        const invite = invites.find(i => i.id === r.inviteeId);
        return {
          name: invite?.name || 'Unknown',
          note: r.additionalNotes,
          status: resolveStatus(r),
        };
      });
    if (notesSearch.trim()) {
      const s = notesSearch.toLowerCase();
      notes = notes.filter(n => n.name.toLowerCase().includes(s) || n.note.toLowerCase().includes(s));
    }
    notes.sort((a, b) => notesSort === 'status'
      ? a.status.localeCompare(b.status)
      : a.name.localeCompare(b.name)
    );
    return notes;
  };

  // ── bringing ──────────────────────────────────────────────
  const getFilteredBringing = () => {
    let items = rsvps
      .filter(r => r.bringingItem && r.bringingItem.trim())
      .map(r => {
        const invite = invites.find(i => i.id === r.inviteeId);
        return {
          name: invite?.name || 'Unknown',
          item: r.bringingItem,
          status: resolveStatus(r),
        };
      });
    if (bringingSearch.trim()) {
      const s = bringingSearch.toLowerCase();
      items = items.filter(i => i.name.toLowerCase().includes(s) || i.item.toLowerCase().includes(s));
    }
    items.sort((a, b) => a.name.localeCompare(b.name));
    return items;
  };

  // ── photo handlers ────────────────────────────────────────
  const handleAddInvite = async (e) => {
    e.preventDefault();
    try {
      await addInvite(newInvite);
      setNewInvite({ name: '', email: '', phone: '' });
      setShowAddForm(false);
    } catch {
      alert('Failed to add invitee. Please try again.');
    }
  };

  const handlePhotoClick = (inviteId, e) => {
    if (showPhotoMenu === inviteId) {
      setShowPhotoMenu(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setPhotoMenuPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
      setShowPhotoMenu(inviteId);
    }
  };

  const handleEditPhoto = (inviteId) => {
    setEditingPhoto(inviteId);
    setShowPhotoMenu(null);
    setTimeout(() => photoFileRef.current?.click(), 50);
  };

  const handleEnlargePhoto = (invite) => {
    setEnlargedPhoto(invite);
    setShowPhotoMenu(null);
  };

  const handleRemovePhoto = async (inviteId) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      const res = await fetch(`${API}/invites/${inviteId}/photo`, { method: 'DELETE', headers: authHeaders() });
      const saved = await res.json();
      setInvitesLocal(saved);
    } catch { /* ignore */ }
    setShowPhotoMenu(null);
  };

  const handlePhotoFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !editingPhoto) return;
    setPhotoUploading(true);
    try {
      const form = new FormData();
      form.append('photo', file);
      const res = await fetch(`${API}/invites/${editingPhoto}/photo`, { method: 'POST', body: form, headers: authHeaders() });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      setInvitesLocal(saved);
    } catch {
      alert('Failed to upload photo.');
    } finally {
      setPhotoUploading(false);
      setEditingPhoto(null);
    }
  };

  // Helper to update a single invite in local state from server response
  const setInvitesLocal = (saved) => {
    updateInvite(saved.id, saved);
  };

  const handleDeleteInvite = async (id, name) => {
    if (window.confirm(`Remove ${name} from the invite list? This will also delete their RSVP.`)) {
      try {
        await deleteInvite(id);
      } catch {
        alert('Failed to remove invitee. Please try again.');
      }
    }
  };

  // ── Excel / CSV import ────────────────────────────────────
  // Reads an uploaded file into rows of [name, email, phone].
  // .csv parsed directly; .xlsx via exceljs (lazy-loaded to keep it out of the main bundle).
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';
    if (!file) return;

    try {
      let rows = []; // array of [name, email, phone]

      if (file.name.toLowerCase().endsWith('.csv')) {
        const text = await file.text();
        rows = text.split(/\r?\n/).map(parseCsvLine);
      } else {
        const mod = await import('exceljs');
        const ExcelJS = mod.default || mod;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(await file.arrayBuffer());
        const sheet = workbook.worksheets[0];
        if (sheet) {
          sheet.eachRow(row => {
            const v = row.values; // 1-indexed: v[1] = column A
            rows.push([cellText(v[1]), cellText(v[2]), cellText(v[3])]);
          });
        }
      }

      const existingNames = invites.map(i => i.name.trim().toLowerCase());
      const toAdd = [];
      const duplicates = [];

      rows.forEach(row => {
        const name = (row[0] || '').toString().trim();
        if (!name || name.toLowerCase() === 'name') return; // skip empty or header row
        const email = (row[1] || '').toString().trim();
        const phone = (row[2] || '').toString().trim();
        if (existingNames.includes(name.toLowerCase())) {
          duplicates.push(name);
        } else {
          toAdd.push({ name, email, phone });
        }
      });

      setExcludedImport(new Set());
      setImportPreview({ toAdd, duplicates });
    } catch {
      alert('Could not read that file. Please use a .xlsx or .csv with columns: Name, Email, Phone.');
    }
  };

  const confirmImport = async () => {
    if (!importPreview) return;
    const finalList = importPreview.toAdd.filter((_, i) => !excludedImport.has(i));
    if (finalList.length === 0) return;
    try {
      await addInvitesBulk(finalList);
      setImportPreview(null);
      setExcludedImport(new Set());
    } catch {
      alert('Failed to import names. Please try again.');
    }
  };

  const filteredInvites = getFilteredInvites();
  const filteredNotes = getFilteredNotes();
  const filteredBringing = getFilteredBringing();

  // ── export CSV ────────────────────────────────────────────
  const exportCSV = () => {
    const headers = ['Name', 'Invite Sent', 'Status', 'Team', 'Bringing', 'Notes', 'Responded At', 'Updated At'];
    const rows = invites
      .slice()
      .sort((a, b) => getLastName(a.name).localeCompare(getLastName(b.name)))
      .map(invite => {
        const rsvp = getRSVP(invite.id);
        const status = getStatus(invite.id);
        return [
          invite.name,
          invite.inviteSent ? 'Yes' : 'No',
          statusLabel(status),
          rsvp?.team || '',
          rsvp?.bringingItem || '',
          rsvp?.additionalNotes || '',
          rsvp?.createdAt ? fmtDateTime(rsvp.createdAt) : '',
          rsvp?.updatedAt ? fmtDateTime(rsvp.updatedAt) : '',
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
      });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'canam-rsvp-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── edit RSVP ─────────────────────────────────────────────
  const openEditRSVP = (invite) => {
    const rsvp = getRSVP(invite.id);
    setEditingRSVP({
      inviteId: invite.id,
      name: invite.name,
      email: invite.email || '',
      phone: invite.phone || '',
      status: rsvp?.status || '',
      team: rsvp?.team === 'Cheerleader' ? '' : (rsvp?.team || ''),
      bringingItem: rsvp?.bringingItem || '',
      additionalNotes: rsvp?.additionalNotes || '',
    });
  };

  const saveEditRSVP = async () => {
    try {
      await updateInvite(editingRSVP.inviteId, {
        name: editingRSVP.name,
        email: editingRSVP.email,
        phone: editingRSVP.phone,
      });
      if (editingRSVP.status) {
        await updateRSVP(editingRSVP.inviteId, {
          status: editingRSVP.status,
          team: editingRSVP.team,
          bringingItem: editingRSVP.bringingItem,
          additionalNotes: editingRSVP.additionalNotes,
        });
      }
      setEditingRSVP(null);
    } catch {
      alert('Failed to save changes. Please try again.');
    }
  };

  // ── donut slice label ─────────────────────────────────────
  const renderDonutLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
    if (!value) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={14} fontWeight="bold">
        {value}
      </text>
    );
  };

  // ── render ────────────────────────────────────────────────
  // Show login screen when not authenticated
  if (!authed) {
    return (
      <div className="page-container">
        <div className="page-content admin-page">
          <div className="admin-login-wrap">
            <h2>Admin Login</h2>
            <form className="admin-login-form" onSubmit={handleLogin}>
              <input
                className="admin-login-input"
                type="password"
                placeholder="Enter admin password"
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                autoFocus
              />
              {loginError && <p className="admin-login-error">{loginError}</p>}
              <button className="admin-login-btn" type="submit" disabled={loginLoading}>
                {loginLoading ? 'Verifying…' : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="page-container">
      <div className="page-content admin-page">
        {/* ── Top header ── */}
        <div className="admin-header-row">
          <h2>Admin Portal</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="admin-settings-btn" onClick={openSettings}>⚙ Settings</button>
            <button className="admin-signout-btn" onClick={handleSignOut}>Sign Out</button>
          </div>
        </div>

        {/* ── Section selector ── */}
        <div className="admin-section-row">
          {[
            { id: 'guests',      label: 'Guests' },
            { id: 'communicate', label: 'Communicate' },
            { id: 'event',       label: 'Event Day' },
          ].map(s => (
            <button key={s.id}
              className={`admin-section-btn${adminSection === s.id ? ' active' : ''}`}
              onClick={() => {
                setAdminSection(s.id);
                const defaults = { guests: 'guests', communicate: 'messages', event: 'gallery' };
                setActiveView(defaults[s.id]);
                if (s.id === 'communicate') { fetchMessages(); fetchScheduled(); if (smsLog.length === 0) fetchSmsLog(); }
                if (s.id === 'event') fetchGallery();
              }}
            >
              {s.label}
              {s.id === 'communicate' && unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
            </button>
          ))}
        </div>

        {/* ── Sub-tabs ── */}
        <div className="admin-tabs-row">
          <div className="admin-tabs">
            {adminSection === 'guests' && (<>
              <button className={`admin-tab${activeView === 'guests' ? ' active' : ''}`} onClick={() => setActiveView('guests')}>Guest List</button>
              <button className={`admin-tab${activeView === 'statistics' ? ' active' : ''}`} onClick={() => setActiveView('statistics')}>Statistics</button>
              <button className={`admin-tab${activeView === 'bringing' ? ' active' : ''}`} onClick={() => setActiveView('bringing')}>
                Bringing {bringingCount > 0 && <span className="tab-badge">{bringingCount}</span>}
              </button>
              <button className={`admin-tab${activeView === 'notes' ? ' active' : ''}`} onClick={() => setActiveView('notes')}>
                Notes {notesCount > 0 && <span className="tab-badge">{notesCount}</span>}
              </button>
              <button className={`admin-tab${activeView === 'auditlog' ? ' active' : ''}`} onClick={() => {
                setActiveView('auditlog');
                if (auditLog.length === 0) {
                  setAuditLoading(true);
                  fetch(`${API}/rsvp-log`, { headers: authHeaders() })
                    .then(r => r.json()).then(data => setAuditLog(data))
                    .catch(() => {}).finally(() => setAuditLoading(false));
                }
              }}>Audit Log</button>
            </>)}
            {adminSection === 'communicate' && (<>
              <button className={`admin-tab${activeView === 'messages' ? ' active' : ''}`} onClick={() => { setActiveView('messages'); fetchMessages(); }}>
                Messages {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
              </button>
              <button className={`admin-tab${activeView === 'sms' ? ' active' : ''}`} onClick={() => { setActiveView('sms'); setSmsSendResult(null); if (smsLog.length === 0) fetchSmsLog(); }}>Send SMS</button>
              <button className={`admin-tab${activeView === 'scheduled' ? ' active' : ''}`} onClick={() => { setActiveView('scheduled'); setSchedDraft(null); fetchScheduled(); }}>Scheduled</button>
            </>)}
            {adminSection === 'event' && (<>
              <button className={`admin-tab${activeView === 'gallery' ? ' active' : ''}`} onClick={() => { setActiveView('gallery'); fetchGallery(); }}>Photos</button>
              <button className={`admin-tab${activeView === 'videos' ? ' active' : ''}`} onClick={() => { setActiveView('videos'); setVideosDraft({ videos: settings.videos || [], featured_video_id: settings.featuredVideoId || null }); }}>Videos</button>
              <button className={`admin-tab${activeView === 'games_admin' ? ' active' : ''}`} onClick={() => { setActiveView('games_admin'); setSelectedGameId(null); setGameDraft(null); setGameAddMode(false); fetchGamesAdmin(); }}>Games</button>
            </>)}
          </div>
          {adminSection === 'guests' && (
            <button className="export-csv-btn" onClick={exportCSV}>⬇ Export CSV</button>
          )}
        </div>

        <div className="admin-tab-content">

        {/* ── GUEST LIST VIEW (merged) ── */}
        {activeView === 'guests' && (<>
          <div className="search-filter-section">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="clear-search">✕</button>
              )}
            </div>
            <div className="filters">
              <div className="filter-group">
                <label>Status:</label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
                  <option value="all">All</option>
                  <option value="playing">Playing</option>
                  <option value="attending">Attending Dinner</option>
                  <option value="unavailable">Unavailable</option>
                  <option value="no-response">No Response</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Contact Info:</label>
                <select value={filterContact} onChange={(e) => setFilterContact(e.target.value)} className="filter-select">
                  <option value="all">All</option>
                  <option value="has-email">Has Email</option>
                  <option value="has-phone">Has Phone</option>
                  <option value="missing-contact">Missing Both</option>
                </select>
              </div>
            </div>
          </div>

          <div className="admin-actions">
            <button onClick={() => setShowAddForm(!showAddForm)} className="add-invite-btn">
              {showAddForm ? 'Cancel' : '+ Add New Invite'}
            </button>
            <button className="import-excel-btn" onClick={() => fileInputRef.current?.click()}>
              📥 Import from Excel
            </button>
            {invites.some(i => !i.inviteSent) && (
              <button
                className="import-excel-btn"
                disabled={markingAllSent}
                onClick={async () => {
                  setMarkingAllSent(true);
                  try {
                    await Promise.all(
                      invites.filter(i => !i.inviteSent).map(i => updateInvite(i.id, { inviteSent: true }))
                    );
                  } catch {
                    alert('Some invites could not be marked as sent.');
                  } finally {
                    setMarkingAllSent(false);
                  }
                }}
              >
                {markingAllSent ? 'Marking…' : '✓ Mark All Sent'}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.csv"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
          </div>

          {/* Import preview */}
          {importPreview && (() => {
            const includedCount = importPreview.toAdd.length - excludedImport.size;
            const toggleExclude = (i) => setExcludedImport(prev => {
              const next = new Set(prev);
              if (next.has(i)) next.delete(i); else next.add(i);
              return next;
            });
            return (
            <div className="import-preview">
              <h3>Import Preview</h3>
              {importPreview.toAdd.length > 0 ? (
                <>
                  <p className="import-count">
                    <strong>{includedCount}</strong> new name{includedCount !== 1 ? 's' : ''} will be added
                    {excludedImport.size > 0 && <span className="import-excluded-note"> · {excludedImport.size} excluded</span>}
                  </p>
                  <p className="import-hint">Click a name to exclude it from the import.</p>
                  <ul className="import-list">
                    {importPreview.toAdd.map((inv, i) => {
                      const excluded = excludedImport.has(i);
                      return (
                        <li
                          key={i}
                          className={`import-item new${excluded ? ' excluded' : ''}`}
                          onClick={() => toggleExclude(i)}
                          title={excluded ? 'Click to include' : 'Click to exclude'}
                        >
                          {inv.name}
                          {inv.email && <span className="import-contact"> — {inv.email}</span>}
                          {inv.phone && <span className="import-contact"> — {inv.phone}</span>}
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : (
                <p className="import-count">No new names to add.</p>
              )}
              {importPreview.duplicates.length > 0 && (
                <>
                  <p className="import-count duplicate-count">
                    <strong>{importPreview.duplicates.length}</strong> duplicate{importPreview.duplicates.length !== 1 ? 's' : ''} skipped
                  </p>
                  <ul className="import-list">
                    {importPreview.duplicates.map((name, i) => (
                      <li key={i} className="import-item duplicate">{name} — already on list</li>
                    ))}
                  </ul>
                </>
              )}
              <div className="import-actions">
                {importPreview.toAdd.length > 0 && (
                  <button className="submit-btn import-confirm-btn" onClick={confirmImport} disabled={includedCount === 0}>
                    Confirm Import{includedCount !== importPreview.toAdd.length ? ` (${includedCount})` : ''}
                  </button>
                )}
                <button className="back-btn" onClick={() => { setImportPreview(null); setExcludedImport(new Set()); }}>Cancel</button>
              </div>
            </div>
            );
          })()}

          {showAddForm && (
            <div className="add-invite-form">
              <h3>Add New Invitee</h3>
              <form onSubmit={handleAddInvite}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      value={newInvite.name}
                      onChange={(e) => setNewInvite({ ...newInvite, name: e.target.value })}
                      required
                      placeholder="First Last"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      value={newInvite.email}
                      onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      value={newInvite.phone}
                      onChange={(e) => setNewInvite({ ...newInvite, phone: e.target.value })}
                      placeholder="555-0000"
                    />
                  </div>
                </div>
                <div className="form-row form-row-bottom">
                  <p className="form-group form-col-span-2" style={{ margin: 0, alignSelf: 'center', color: '#888', fontSize: '0.85rem' }}>
                    Add a photo after creating the guest — click their avatar in the list below.
                  </p>
                  <div className="form-group form-submit-col">
                    <button type="submit" className="submit-btn">Add Invitee</button>
                  </div>
                </div>
              </form>
            </div>
          )}

          <div className="invites-table-container">
            <div className="table-header">
              <h3>Guest List</h3>
              <span className="result-count">
                Showing {filteredInvites.length} of {invites.length} invitees
              </span>
            </div>
            <div className="table-scroll">
              <table className="invites-table">
                <thead>
                  <tr>
                    <th>Photo</th>
                    <th>Name</th>
                    <th>Invite Sent</th>
                    <th>Status</th>
                    <th>Team</th>
                    <th>Bringing</th>
                    <th>Notes</th>
                    <th>Responded</th>
                    <th>Last Updated</th>
                    <th>Edit</th>
                    <th>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvites.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="no-results">
                        <div className="no-results-message">
                          {searchTerm || filterStatus !== 'all' || filterContact !== 'all' ? (
                            <>
                              <p>No invitees match your filters.</p>
                              <button
                                onClick={() => { setSearchTerm(''); setFilterStatus('all'); setFilterContact('all'); }}
                                className="clear-filters-btn"
                              >
                                Clear All Filters
                              </button>
                            </>
                          ) : (
                            <p>No invitees yet. Add one to get started!</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredInvites.map(invite => {
                      const rsvp = getRSVP(invite.id);
                      const status = getStatus(invite.id);
                      return (
                        <tr key={invite.id}>
                          <td>
                            {photoUploading && editingPhoto === invite.id ? (
                              <div className="no-photo" style={{ fontSize: '0.65rem', color: '#667eea' }}>Uploading…</div>
                            ) : (
                              <div className="photo-cell">
                                <div
                                  className="photo-preview clickable"
                                  onClick={(e) => { e.stopPropagation(); handlePhotoClick(invite.id, e); }}
                                  title="Click to manage photo"
                                >
                                  {invite.photoUrl ? (
                                    <img src={resolvePhoto(invite.photoUrl)} alt={invite.name} className="admin-photo" />
                                  ) : (
                                    <div className="no-photo">Click to add</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="name-cell">
                            <button
                              className="name-contact-btn"
                              onClick={(e) => { e.stopPropagation(); setContactPopup(contactPopup === invite.id ? null : invite.id); }}
                              title="Click to view contact info"
                            >
                              {invite.name}
                            </button>
                            {contactPopup === invite.id && (
                              <div className="contact-popup">
                                {invite.email ? <div>📧 {invite.email}</div> : null}
                                {invite.phone ? <div>📞 {fmtPhone(invite.phone)}</div> : null}
                                {!invite.email && !invite.phone && <div className="no-contact">No contact info</div>}
                              </div>
                            )}
                          </td>
                          <td>
                            <button
                              className={`sent-toggle ${invite.inviteSent ? 'sent' : 'not-sent'}`}
                              onClick={() => toggleInviteSent(invite.id)}
                              title={invite.inviteSent ? 'Mark as not sent' : 'Mark as sent'}
                            >
                              {invite.inviteSent ? '✓ Sent' : 'Not Sent'}
                            </button>
                          </td>
                          <td>
                            <span className={`status-badge status-${status}`}>
                              {statusLabel(status)}
                            </span>
                          </td>
                          <td>{rsvp?.team || '—'}</td>
                          <td>{rsvp?.bringingItem || '—'}</td>
                          <td className="note-cell">{rsvp?.additionalNotes || '—'}</td>
                          <td className="timestamp-cell">{fmtDate(rsvp?.createdAt)}</td>
                          <td className="timestamp-cell">{fmtDate(rsvp?.updatedAt)}</td>
                          <td>
                            <button className="edit-rsvp-btn" onClick={() => openEditRSVP(invite)}>
                              Edit
                            </button>
                          </td>
                          <td>
                            <button className="delete-btn" onClick={() => handleDeleteInvite(invite.id, invite.name)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>)}

        {/* ── STATISTICS VIEW ── */}
        {activeView === 'statistics' && (
          <div className="statistics-section">

            {/* Summary numbers */}
            <div className="stats-summary-row">
              <div className="summary-chip"><span className="summary-val">{totalInvites}</span><span className="summary-lbl">Total Invites</span></div>
              <div className="summary-chip"><span className="summary-val">{invitesSent}</span><span className="summary-lbl">Invites Sent</span></div>
              <div className="summary-chip"><span className="summary-val">{responded}</span><span className="summary-lbl">Responded</span></div>
              <div className="summary-chip"><span className="summary-val">{noResponse}</span><span className="summary-lbl">No Response</span></div>
              <div className="summary-chip"><span className="summary-val">{playingCount + attendingCount}</span><span className="summary-lbl">Attending</span></div>
              <div className="summary-chip"><span className="summary-val">{unavailableCount}</span><span className="summary-lbl">Unavailable</span></div>
            </div>

            <div className="charts-grid">
              {/* Response breakdown pie */}
              <div className="chart-card">
                <h4 className="chart-title">Response Breakdown</h4>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Playing', value: playingCount },
                        { name: 'Attending Dinner', value: attendingCount },
                        { name: 'Unavailable', value: unavailableCount },
                        { name: 'No Response', value: noResponse },
                      ].filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                      label={renderDonutLabel}
                      labelLine={false}
                    >
                      <Cell fill="#1565c0" />
                      <Cell fill="#2e7d32" />
                      <Cell fill="#c62828" />
                      <Cell fill="#757575" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Team breakdown pie */}
              <div className="chart-card">
                <h4 className="chart-title">Team Breakdown</h4>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Team USA', value: usaCount },
                        { name: 'Team Canada', value: canadaCount },
                        { name: 'Cheerleaders', value: cheerleaderCount },
                      ].filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                      label={renderDonutLabel}
                      labelLine={false}
                    >
                      <Cell fill="#1565c0" />
                      <Cell fill="#c62828" />
                      <Cell fill="#f9a825" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Response bar chart */}
              <div className="chart-card chart-card-wide">
                <h4 className="chart-title">Attendance Overview</h4>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={[
                      { name: 'Playing', count: playingCount, fill: '#1565c0' },
                      { name: 'Dinner', count: attendingCount, fill: '#2e7d32' },
                      { name: 'Unavailable', count: unavailableCount, fill: '#c62828' },
                      { name: 'No Response', count: noResponse, fill: '#bdbdbd' },
                      { name: 'Team USA', count: usaCount, fill: '#1565c0' },
                      { name: 'Team Canada', count: canadaCount, fill: '#c62828' },
                      { name: 'Cheerleaders', count: cheerleaderCount, fill: '#f9a825' },
                    ]}
                    margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {[
                        { name: 'Playing', fill: '#1565c0' },
                        { name: 'Dinner', fill: '#2e7d32' },
                        { name: 'Unavailable', fill: '#c62828' },
                        { name: 'No Response', fill: '#757575' },
                        { name: 'Team USA', fill: '#1565c0' },
                        { name: 'Team Canada', fill: '#c62828' },
                        { name: 'Cheerleaders', fill: '#f9a825' },
                      ].map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                      <LabelList dataKey="count" position="top" style={{ fontSize: 13, fontWeight: 'bold', fill: '#333' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* ── BRINGING VIEW ── */}
        {activeView === 'bringing' && (
          <div className="notes-section">
            <div className="notes-controls">
              <input
                type="text"
                className="search-input"
                placeholder="Search by name or item..."
                value={bringingSearch}
                onChange={(e) => setBringingSearch(e.target.value)}
              />
            </div>
            {filteredBringing.length === 0 ? (
              <p className="no-notes-msg">
                {bringingSearch ? 'No results match your search.' : 'Nobody has listed what they are bringing yet.'}
              </p>
            ) : (
              <div className="notes-list">
                {filteredBringing.map((b, i) => (
                  <div key={i} className="note-card">
                    <div className="note-card-header">
                      <span className="note-name">{b.name}</span>
                      <span className={`status-badge status-${b.status}`}>
                        {statusLabel(b.status)}
                      </span>
                    </div>
                    <p className="note-text">{b.item}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── NOTES VIEW ── */}
        {activeView === 'notes' && (
          <div className="notes-section">
            <div className="notes-controls">
              <input
                type="text"
                className="search-input"
                placeholder="Search by name or note content..."
                value={notesSearch}
                onChange={(e) => setNotesSearch(e.target.value)}
              />
              <div className="filter-group">
                <label>Sort:</label>
                <select className="filter-select" value={notesSort} onChange={(e) => setNotesSort(e.target.value)}>
                  <option value="name">Name</option>
                  <option value="status">Status</option>
                </select>
              </div>
            </div>
            {filteredNotes.length === 0 ? (
              <p className="no-notes-msg">
                {notesSearch ? 'No notes match your search.' : 'No notes have been left yet.'}
              </p>
            ) : (
              <div className="notes-list">
                {filteredNotes.map((n, i) => (
                  <div key={i} className="note-card">
                    <div className="note-card-header">
                      <span className="note-name">{n.name}</span>
                      <span className={`status-badge status-${n.status}`}>
                        {statusLabel(n.status)}
                      </span>
                    </div>
                    <p className="note-text">{n.note}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* ── MESSAGES VIEW ── */}
        {activeView === 'messages' && (
          <div className="notes-section">
            <div className="audit-log-header">
              <h3>Guest Messages</h3>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="audit-refresh-btn" onClick={fetchMessages}>↻ Refresh</button>
                {messages.some(m => !m.isRead) && (
                  <button className="audit-refresh-btn" onClick={markAllRead}>✓ Mark All Read</button>
                )}
              </div>
            </div>
            {messagesLoading ? (
              <p className="audit-loading">Loading messages…</p>
            ) : messages.length === 0 ? (
              <p className="no-notes-msg">No messages yet.</p>
            ) : (
              <div className="notes-list">
                {messages.map(msg => (
                  <div key={msg.id} className={`note-card message-card${msg.isRead ? '' : ' message-unread'}`}>
                    <div className="note-card-header">
                      <div>
                        <span className="note-name">{msg.name}</span>
                        {msg.typedName && msg.typedName !== msg.name && (
                          <span className="message-typed-name"> (searched for "{msg.typedName}")</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {!msg.isRead && <span className="message-unread-dot">New</span>}
                        <span className="timestamp-cell">{fmtDateTime(msg.createdAt)}</span>
                        <button className="delete-btn" onClick={() => deleteMessage(msg.id)}>
                          Delete
                        </button>
                      </div>
                    </div>
                    <p className="note-text">{msg.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── AUDIT LOG VIEW ── */}
        {activeView === 'auditlog' && (
          <div className="audit-log-section">
            <div className="audit-log-header">
              <h3>RSVP Audit Log</h3>
              <button
                className="audit-refresh-btn"
                onClick={() => {
                  setAuditLoading(true);
                  fetch(`${API}/rsvp-log`, { headers: authHeaders() })
                    .then(r => r.json())
                    .then(data => setAuditLog(data))
                    .catch(() => {})
                    .finally(() => setAuditLoading(false));
                }}
              >
                ↻ Refresh
              </button>
            </div>
            {auditLoading ? (
              <p className="audit-loading">Loading log…</p>
            ) : auditLog.length === 0 ? (
              <p className="no-notes-msg">No audit entries yet.</p>
            ) : (
              <div className="table-scroll">
                <table className="invites-table audit-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Name</th>
                      <th>Action</th>
                      <th>Status</th>
                      <th>Team</th>
                      <th>Bringing</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.map(entry => (
                      <tr key={entry.id}>
                        <td className="timestamp-cell">
                          {fmtDateTime(entry.logged_at)}
                        </td>
                        <td>{entry.invitee_name}</td>
                        <td>
                          <span className={`audit-action-badge audit-${entry.action}`}>
                            {entry.action}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge status-${entry.status}`}>
                            {statusLabel(entry.status)}
                          </span>
                        </td>
                        <td>{entry.team || '—'}</td>
                        <td>{entry.bringing_item || '—'}</td>
                        <td className="note-cell">{entry.additional_notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── SETTINGS VIEW ── */}
        {settingsOpen && settingsDraft && (
          <div className="settings-modal-overlay" onClick={() => setSettingsOpen(false)}>
            <div className="settings-modal-card" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0 }}>Event Settings</h3>
              <button onClick={() => setSettingsOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#888', lineHeight: 1 }}>✕</button>
            </div>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Changes here update the countdown, RSVP deadline, and calendar links across the entire site.
            </p>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setSettingsSaving(true);
                setSettingsSaved(false);
                try {
                  await updateSettings({
                    ...settingsDraft,
                    schedule:   JSON.stringify(settingsDraft.schedule   || []),
                    highlights: JSON.stringify(settingsDraft.highlights || []),
                  });
                  setSettingsSaved(true);
                  setTimeout(() => setSettingsSaved(false), 3000);
                } catch {
                  alert('Failed to save settings. Please try again.');
                } finally {
                  setSettingsSaving(false);
                }
              }}
              style={{ maxWidth: '420px' }}
            >
              <div className="form-group">
                <label>Event Start</label>
                <input
                  type="datetime-local"
                  value={settingsDraft.event_date}
                  onChange={e => setSettingsDraft(d => ({ ...d, event_date: e.target.value }))}
                  className="search-input"
                  required
                />
              </div>
              <div className="form-group">
                <label>Event End</label>
                <input
                  type="datetime-local"
                  value={settingsDraft.event_end_date}
                  onChange={e => setSettingsDraft(d => ({ ...d, event_end_date: e.target.value }))}
                  className="search-input"
                  required
                />
              </div>
              <div className="form-group">
                <label>RSVP Deadline</label>
                <input
                  type="datetime-local"
                  value={settingsDraft.rsvp_deadline}
                  onChange={e => setSettingsDraft(d => ({ ...d, rsvp_deadline: e.target.value }))}
                  className="search-input"
                  required
                />
              </div>
              <div className="form-group">
                <label>Enforce RSVP Deadline</label>
                <div className="deadline-toggle-row">
                  <button
                    type="button"
                    className={`deadline-toggle${settingsDraft.enforce_deadline ? ' on' : ' off'}`}
                    onClick={() => setSettingsDraft(d => ({ ...d, enforce_deadline: !d.enforce_deadline }))}
                  >
                    <span className="deadline-toggle-knob" />
                  </button>
                  <span className="deadline-toggle-label">
                    {settingsDraft.enforce_deadline
                      ? 'On — RSVPs close after the deadline'
                      : 'Off — RSVPs stay open past the deadline'}
                  </span>
                </div>
              </div>
              <div className="form-group">
                <label>Event Description</label>
                <input
                  type="text"
                  value={settingsDraft.event_description}
                  onChange={e => setSettingsDraft(d => ({ ...d, event_description: e.target.value }))}
                  className="search-input"
                  placeholder="Annual CanAm Event — Team USA vs Team Canada!"
                />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={settingsDraft.event_location}
                  onChange={e => setSettingsDraft(d => ({ ...d, event_location: e.target.value }))}
                  className="search-input"
                  placeholder="137 Northington Dr., East Amherst, NY 14051"
                />
              </div>
              <div className="form-group">
                <label>Attire <span style={{ color: '#aaa', fontWeight: 400, fontSize: '0.85rem' }}>(optional — leave blank to hide)</span></label>
                <input
                  type="text"
                  value={settingsDraft.attire}
                  onChange={e => setSettingsDraft(d => ({ ...d, attire: e.target.value }))}
                  className="search-input"
                  placeholder="e.g. Casual — wear your USA or Canada colors!"
                />
              </div>

              <hr style={{ margin: '1.5rem 0', borderColor: '#e0e0e0' }} />

              <div className="form-group" style={{ maxWidth: '100%' }}>
                <label style={{ fontSize: '1rem', fontWeight: 600, color: '#333' }}>What to Expect</label>
                <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', marginBottom: '1rem' }}>
                  These bullet points appear on the Invite page.
                </p>
                {(settingsDraft.highlights || []).map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', marginBottom: '0.5rem', background: '#f8f8f8', borderRadius: '8px', border: '1.5px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                      <button type="button" disabled={idx === 0} style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', cursor: idx === 0 ? 'default' : 'pointer', padding: '1px 6px', fontSize: '0.7rem', color: idx === 0 ? '#ccc' : '#555', lineHeight: 1 }}
                        onClick={() => setSettingsDraft(d => { const a = [...d.highlights]; [a[idx-1], a[idx]] = [a[idx], a[idx-1]]; return { ...d, highlights: a }; })}>▲</button>
                      <button type="button" disabled={idx === settingsDraft.highlights.length - 1} style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', cursor: idx === settingsDraft.highlights.length - 1 ? 'default' : 'pointer', padding: '1px 6px', fontSize: '0.7rem', color: idx === settingsDraft.highlights.length - 1 ? '#ccc' : '#555', lineHeight: 1 }}
                        onClick={() => setSettingsDraft(d => { const a = [...d.highlights]; [a[idx], a[idx+1]] = [a[idx+1], a[idx]]; return { ...d, highlights: a }; })}>▼</button>
                    </div>
                    <span style={{ flex: 1, color: '#333', fontSize: '0.9rem' }}>{item}</span>
                    <button type="button" className="delete-btn"
                      onClick={() => setSettingsDraft(d => ({ ...d, highlights: d.highlights.filter((_, i) => i !== idx) }))}>
                      Remove
                    </button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'flex-end' }}>
                  <input
                    type="text"
                    placeholder="e.g. Live music all night"
                    value={newHighlightForm}
                    onChange={e => setNewHighlightForm(e.target.value)}
                    className="search-input"
                    style={{ flex: 1 }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); if (newHighlightForm.trim()) { setSettingsDraft(d => ({ ...d, highlights: [...(d.highlights || []), newHighlightForm.trim()] })); setNewHighlightForm(''); } }
                    }}
                  />
                  <button type="button" className="add-invite-btn" style={{ flexShrink: 0, height: '38px' }}
                    onClick={() => {
                      if (!newHighlightForm.trim()) return;
                      setSettingsDraft(d => ({ ...d, highlights: [...(d.highlights || []), newHighlightForm.trim()] }));
                      setNewHighlightForm('');
                    }}>
                    + Add
                  </button>
                </div>
              </div>

              <hr style={{ margin: '1.5rem 0', borderColor: '#e0e0e0' }} />

              <div className="form-group" style={{ maxWidth: '100%' }}>
                <label style={{ fontSize: '1rem', fontWeight: 600, color: '#333' }}>Event Schedule</label>
                <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', marginBottom: '1rem' }}>
                  These items appear on the Invite page in order.
                </p>

                {(settingsDraft.schedule || []).map((item, idx) => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.75rem', marginBottom: '0.5rem', background: '#f8f8f8', borderRadius: '8px', border: '1.5px solid #e0e0e0', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                      <button type="button" disabled={idx === 0} style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', cursor: idx === 0 ? 'default' : 'pointer', padding: '1px 6px', fontSize: '0.7rem', color: idx === 0 ? '#ccc' : '#555', lineHeight: 1 }}
                        onClick={() => setSettingsDraft(d => { const arr = [...d.schedule]; [arr[idx-1], arr[idx]] = [arr[idx], arr[idx-1]]; return { ...d, schedule: arr }; })}>▲</button>
                      <button type="button" disabled={idx === settingsDraft.schedule.length - 1} style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', cursor: idx === settingsDraft.schedule.length - 1 ? 'default' : 'pointer', padding: '1px 6px', fontSize: '0.7rem', color: idx === settingsDraft.schedule.length - 1 ? '#ccc' : '#555', lineHeight: 1 }}
                        onClick={() => setSettingsDraft(d => { const arr = [...d.schedule]; [arr[idx], arr[idx+1]] = [arr[idx+1], arr[idx]]; return { ...d, schedule: arr }; })}>▼</button>
                    </div>
                    <span style={{ fontWeight: 600, color: '#667eea', minWidth: '80px', flexShrink: 0, fontSize: '0.9rem' }}>{item.time}</span>
                    <span style={{ flex: 1, color: '#333', fontSize: '0.9rem' }}>{item.description}</span>
                    <button type="button" className="delete-btn" style={{ flexShrink: 0 }}
                      onClick={() => setSettingsDraft(d => ({ ...d, schedule: d.schedule.filter(x => x.id !== item.id) }))}>
                      Remove
                    </button>
                  </div>
                ))}

                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '0 0 120px' }}>
                    <label style={{ fontSize: '0.8rem', color: '#666' }}>Time</label>
                    <input type="text" placeholder="e.g. 3:00 PM" value={newScheduleForm.time}
                      onChange={e => setNewScheduleForm(f => ({ ...f, time: e.target.value }))} className="search-input" />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '3 1 240px' }}>
                    <label style={{ fontSize: '0.8rem', color: '#666' }}>Description</label>
                    <input type="text" placeholder="What's happening?" value={newScheduleForm.description}
                      onChange={e => setNewScheduleForm(f => ({ ...f, description: e.target.value }))} className="search-input" />
                  </div>
                  <button type="button" className="add-invite-btn" style={{ flexShrink: 0, height: '38px' }}
                    onClick={() => {
                      if (!newScheduleForm.time.trim() || !newScheduleForm.description.trim()) return;
                      const newItem = { id: String(Date.now()), time: newScheduleForm.time.trim(), description: newScheduleForm.description.trim() };
                      setSettingsDraft(d => ({ ...d, schedule: [...(d.schedule || []), newItem] }));
                      setNewScheduleForm({ time: '', description: '' });
                    }}>
                    + Add
                  </button>
                </div>
              </div>

              <button type="submit" className="submit-btn" disabled={settingsSaving} style={{ marginTop: '1rem' }}>
                {settingsSaving ? 'Saving…' : settingsSaved ? '✓ Saved!' : 'Save Changes'}
              </button>
            </form>

            <hr style={{ margin: '2.5rem 0', borderColor: '#e0e0e0' }} />

            <h3 style={{ color: '#c62828' }}>Danger Zone</h3>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              Use this at the start of a new year to wipe all invites and RSVPs so you can start fresh.
              <br /><strong>This cannot be undone.</strong>
            </p>
            {!clearConfirm ? (
              <button
                className="submit-btn"
                style={{ background: '#c62828' }}
                onClick={() => setClearConfirm(true)}
              >
                Clear Guest List
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ color: '#c62828', fontWeight: 600 }}>Are you sure? This deletes everyone.</span>
                <button
                  className="submit-btn"
                  style={{ background: '#c62828' }}
                  disabled={clearing}
                  onClick={async () => {
                    setClearing(true);
                    try {
                      await clearGuestList();
                      setClearConfirm(false);
                    } catch {
                      alert('Failed to clear guest list.');
                    } finally {
                      setClearing(false);
                    }
                  }}
                >
                  {clearing ? 'Clearing…' : 'Yes, Clear Everything'}
                </button>
                <button
                  className="back-btn"
                  onClick={() => setClearConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            )}
            </div>
          </div>
        )}

        {/* ── VIDEOS VIEW ── */}
        {activeView === 'videos' && videosDraft && (
          <div className="dashboard-section">
            <h3>Event Videos</h3>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Add a YouTube link for each year's video. Select one as the featured (large) video shown at the top of the Videos page. All others appear as clickable thumbnails below it.
            </p>

            {videosDraft.videos.length === 0 && (
              <p style={{ color: '#aaa', fontStyle: 'italic', marginBottom: '1rem' }}>No videos added yet.</p>
            )}

            {videosDraft.videos.map((v, idx) => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', marginBottom: '0.6rem', background: videosDraft.featured_video_id === v.id ? '#f0f7ff' : '#f8f8f8', borderRadius: '10px', border: videosDraft.featured_video_id === v.id ? '1.5px solid #667eea' : '1.5px solid #e0e0e0', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                  <button
                    title="Move up"
                    disabled={idx === 0}
                    style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', cursor: idx === 0 ? 'default' : 'pointer', padding: '1px 6px', fontSize: '0.7rem', color: idx === 0 ? '#ccc' : '#555', lineHeight: 1 }}
                    onClick={() => setVideosDraft(d => {
                      const arr = [...d.videos];
                      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                      return { ...d, videos: arr };
                    })}
                  >▲</button>
                  <button
                    title="Move down"
                    disabled={idx === videosDraft.videos.length - 1}
                    style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', cursor: idx === videosDraft.videos.length - 1 ? 'default' : 'pointer', padding: '1px 6px', fontSize: '0.7rem', color: idx === videosDraft.videos.length - 1 ? '#ccc' : '#555', lineHeight: 1 }}
                    onClick={() => setVideosDraft(d => {
                      const arr = [...d.videos];
                      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                      return { ...d, videos: arr };
                    })}
                  >▼</button>
                </div>
                <input
                  type="radio"
                  name="featured_video"
                  title="Set as featured video"
                  checked={videosDraft.featured_video_id === v.id}
                  onChange={() => setVideosDraft(d => ({ ...d, featured_video_id: v.id }))}
                  style={{ flexShrink: 0, cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, color: '#333' }}>{v.title}</span>
                  <span style={{ marginLeft: '0.6rem', color: '#888', fontSize: '0.85rem' }}>{v.year}</span>
                  <div style={{ fontSize: '0.78rem', color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '0.15rem' }}>{v.youtubeUrl}</div>
                </div>
                {videosDraft.featured_video_id === v.id && (
                  <span style={{ fontSize: '0.75rem', background: '#667eea', color: '#fff', borderRadius: '4px', padding: '0.2rem 0.6rem', flexShrink: 0 }}>Featured</span>
                )}
                <button
                  className="delete-btn"
                  style={{ flexShrink: 0 }}
                  onClick={() => setVideosDraft(d => {
                    const remaining = d.videos.filter(x => x.id !== v.id);
                    return {
                      ...d,
                      videos: remaining,
                      featured_video_id: d.featured_video_id === v.id ? (remaining[0]?.id || null) : d.featured_video_id,
                    };
                  })}
                >
                  Remove
                </button>
              </div>
            ))}

            <hr style={{ margin: '1.5rem 0', borderColor: '#e0e0e0' }} />
            <h4 style={{ marginBottom: '0.75rem', color: '#555' }}>Add a Video</h4>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '2 1 180px' }}>
                <label style={{ fontSize: '0.8rem', color: '#666' }}>Title</label>
                <input
                  type="text"
                  placeholder="e.g. 2025 Event Promo"
                  value={newVideoForm.title}
                  onChange={e => setNewVideoForm(f => ({ ...f, title: e.target.value }))}
                  className="search-input"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '0 0 120px' }}>
                <label style={{ fontSize: '0.8rem', color: '#666' }}>Year</label>
                <input
                  type="number"
                  placeholder={new Date().getFullYear()}
                  value={newVideoForm.year}
                  onChange={e => setNewVideoForm(f => ({ ...f, year: e.target.value }))}
                  className="search-input"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '2 1 180px' }}>
                <label style={{ fontSize: '0.8rem', color: '#666' }}>YouTube URL</label>
                <input
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={newVideoForm.youtubeUrl}
                  onChange={e => setNewVideoForm(f => ({ ...f, youtubeUrl: e.target.value }))}
                  className="search-input"
                />
              </div>
              <button
                className="add-invite-btn"
                style={{ flexShrink: 0, height: '38px' }}
                onClick={() => {
                  if (!newVideoForm.title.trim() || !newVideoForm.youtubeUrl.trim()) return;
                  const newVideo = {
                    id: String(Date.now()),
                    title: newVideoForm.title.trim(),
                    year: newVideoForm.year || String(new Date().getFullYear()),
                    youtubeUrl: newVideoForm.youtubeUrl.trim(),
                  };
                  setVideosDraft(d => ({
                    ...d,
                    videos: [...d.videos, newVideo],
                    featured_video_id: d.featured_video_id || newVideo.id,
                  }));
                  setNewVideoForm({ title: '', year: '', youtubeUrl: '' });
                }}
              >
                + Add Video
              </button>
            </div>

            <button
              className="submit-btn"
              style={{ marginTop: '1.5rem', maxWidth: '200px' }}
              disabled={videosSaving}
              onClick={async () => {
                setVideosSaving(true);
                setVideosSaved(false);
                try {
                  await updateSettings({
                    videos: JSON.stringify(videosDraft.videos),
                    featured_video_id: videosDraft.featured_video_id || '',
                  });
                  setVideosSaved(true);
                  setTimeout(() => setVideosSaved(false), 3000);
                } catch {
                  alert('Failed to save videos. Please try again.');
                } finally {
                  setVideosSaving(false);
                }
              }}
            >
              {videosSaving ? 'Saving…' : videosSaved ? '✓ Saved!' : 'Save Videos'}
            </button>
          </div>
        )}

        {activeView === 'sms' && (() => {
          const info = getFinalRecipientInfo();
          const segments = Math.ceil(smsMessage.length / 160) || 1;
          return (
            <div>
              <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Send Text Message</h3>

              <div className="form-group">
                <label>Recipients</label>
                <select
                  value={smsFilter}
                  onChange={e => { setSmsFilter(e.target.value); setSmsSendResult(null); }}
                  className="filter-select"
                  style={{ width: '100%', maxWidth: 280 }}
                >
                  {SMS_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem', color: '#555' }}>
                  <strong>{info.includedCount}</strong> will receive this message
                  {info.skippedNoPhone > 0 && <span style={{ color: '#888' }}> · {info.skippedNoPhone} skipped (no phone number)</span>}
                  {overrideCount > 0 && <span style={{ color: '#3c5bc4' }}> · {overrideCount} manual override{overrideCount !== 1 ? 's' : ''}</span>}
                </p>
                {!info.isTest && (
                  <button
                    type="button"
                    onClick={() => { setRecipientSearch(''); setRecipientModalOpen(true); }}
                    style={{ marginTop: '0.5rem', background: '#fff', border: '1.5px solid #ccc', borderRadius: 6, padding: '0.4rem 0.85rem', fontSize: '0.85rem', color: '#444', cursor: 'pointer' }}
                  >
                    👥 Review / edit recipients
                  </button>
                )}
              </div>

              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Message</span>
                  <span style={{ color: smsMessage.length > 160 ? '#b26a00' : '#888', fontWeight: 400, fontSize: '0.88rem' }}>
                    {smsMessage.length} chars · {segments} segment{segments !== 1 ? 's' : ''}
                  </span>
                </label>
                <textarea
                  value={smsMessage}
                  onChange={e => setSmsMessage(e.target.value)}
                  rows={5}
                  maxLength={480}
                  placeholder="Type your message here…"
                  style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.95rem', padding: '0.6rem', borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={smsIncludeRsvpLink}
                  onChange={e => setSmsIncludeRsvpLink(e.target.checked)}
                />
                Append RSVP link <span style={{ color: '#888', fontSize: '0.82rem' }}>(adds "RSVP here: canamgames.fun/rsvp")</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', fontSize: '0.9rem', cursor: featuredVideo ? 'pointer' : 'default', opacity: featuredVideo ? 1 : 0.5 }}>
                <input
                  type="checkbox"
                  checked={smsIncludeVideoLink}
                  disabled={!featuredVideo}
                  onChange={e => setSmsIncludeVideoLink(e.target.checked)}
                />
                Append featured video link <span style={{ color: '#888', fontSize: '0.82rem' }}>
                  {featuredVideo ? `(${featuredVideo.title})` : '(no featured video set)'}
                </span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={smsMarkInviteSent}
                  onChange={e => setSmsMarkInviteSent(e.target.checked)}
                />
                Mark recipients as "Invite Sent" after sending
              </label>

              {smsSendResult && (
                <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: 6, background: smsSendResult.error ? '#fdecea' : '#e8f5e9', color: smsSendResult.error ? '#c62828' : '#2e7d32', fontWeight: 500 }}>
                  {smsSendResult.error
                    ? `Error: ${smsSendResult.error}`
                    : `Sent ${smsSendResult.sent} · Skipped ${smsSendResult.skipped} (no phone) · Failed ${smsSendResult.failed}`}
                </div>
              )}

              <button
                className="submit-btn"
                onClick={handleSmsSend}
                disabled={smsSending || !smsMessage.trim() || info.includedCount === 0}
              >
                {smsSending ? 'Sending…' : `Send to ${info.includedCount} recipient${info.includedCount !== 1 ? 's' : ''}`}
              </button>

              <hr style={{ margin: '2rem 0', borderColor: '#e0e0e0' }} />

              <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Send History</h3>
              {smsLogLoading ? (
                <p style={{ color: '#888' }}>Loading…</p>
              ) : smsLog.length === 0 ? (
                <p style={{ color: '#888' }}>No messages sent yet.</p>
              ) : (
                <div className="table-scroll">
                  <table className="invites-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Filter</th>
                        <th>Sent</th>
                        <th>Skipped</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {smsLog.map(l => (
                        <tr key={l.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(l.sent_at)}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{l.filter_used}</td>
                          <td>{l.sent_count}</td>
                          <td>{l.skipped_count}</td>
                          <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── SCHEDULED MESSAGES TAB ── */}
        {activeView === 'scheduled' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Scheduled Messages</h3>
              {!schedDraft && (
                <button className="add-invite-btn" onClick={() => setSchedDraft(newSchedDraft())}>+ New Scheduled Message</button>
              )}
            </div>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Queue texts to send automatically at a set date and time. Toggle one off and it won't send when its time arrives.
            </p>

            {/* Editor form */}
            {schedDraft && (() => {
              const { withPhone, skipped } = getSmsRecipients(schedDraft.filter);
              const segments = Math.ceil(schedDraft.message.length / 160) || 1;
              return (
                <div style={{ background: '#f8f8f8', border: '1.5px solid #e0e0e0', borderRadius: 10, padding: '1.25rem', marginBottom: '2rem' }}>
                  <h4 style={{ margin: '0 0 1rem' }}>{schedDraft.id ? 'Edit Message' : 'New Message'}</h4>

                  <div className="form-group">
                    <label>Label <span style={{ color: '#aaa', fontWeight: 400 }}>(for your reference only)</span></label>
                    <input className="search-input" placeholder="e.g. RSVP deadline reminder" value={schedDraft.label}
                      onChange={e => setSchedDraft(d => ({ ...d, label: e.target.value }))} />
                  </div>

                  <div className="form-group">
                    <label>Recipients</label>
                    <select className="filter-select" style={{ width: '100%', maxWidth: 320 }} value={schedDraft.filter}
                      onChange={e => setSchedDraft(d => ({ ...d, filter: e.target.value }))}>
                      {SMS_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <p style={{ margin: '0.4rem 0 0', fontSize: '0.88rem', color: '#555' }}>
                      <strong>{withPhone.length}</strong> will receive this
                      {skipped.length > 0 && <span style={{ color: '#888' }}> · {skipped.length} skipped (no phone)</span>}
                      <span style={{ color: '#999' }}> — counted at send time</span>
                    </p>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Message</span>
                      <span style={{ color: schedDraft.message.length > 160 ? '#b26a00' : '#888', fontWeight: 400, fontSize: '0.88rem' }}>
                        {schedDraft.message.length} chars · {segments} segment{segments !== 1 ? 's' : ''}
                      </span>
                    </label>
                    <textarea value={schedDraft.message} rows={4} maxLength={480} placeholder="Type your message here…"
                      onChange={e => setSchedDraft(d => ({ ...d, message: e.target.value }))}
                      style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.95rem', padding: '0.6rem', borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }} />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={schedDraft.includeRsvpLink}
                      onChange={e => setSchedDraft(d => ({ ...d, includeRsvpLink: e.target.checked }))} />
                    Append RSVP link <span style={{ color: '#888', fontSize: '0.82rem' }}>(adds "RSVP here: canamgames.fun/rsvp")</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', fontSize: '0.9rem', cursor: featuredVideo ? 'pointer' : 'default', opacity: featuredVideo ? 1 : 0.5 }}>
                    <input type="checkbox" checked={schedDraft.includeVideoLink} disabled={!featuredVideo}
                      onChange={e => setSchedDraft(d => ({ ...d, includeVideoLink: e.target.checked }))} />
                    Append featured video link <span style={{ color: '#888', fontSize: '0.82rem' }}>
                      {featuredVideo ? `(${featuredVideo.title})` : '(no featured video set)'}
                    </span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={schedDraft.markInviteSent}
                      onChange={e => setSchedDraft(d => ({ ...d, markInviteSent: e.target.checked }))} />
                    Mark recipients as "Invite Sent" after sending
                  </label>

                  <div className="form-group">
                    <label>Send at</label>
                    <input type="datetime-local" className="search-input" style={{ maxWidth: 260 }} value={schedDraft.sendAtLocal}
                      onChange={e => setSchedDraft(d => ({ ...d, sendAtLocal: e.target.value }))} />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={schedDraft.enabled}
                      onChange={e => setSchedDraft(d => ({ ...d, enabled: e.target.checked }))} />
                    Enabled (will send at the scheduled time)
                  </label>

                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button className="submit-btn" onClick={saveSchedDraft} disabled={schedSaving || !schedDraft.message.trim() || !schedDraft.sendAtLocal}>
                      {schedSaving ? 'Saving…' : schedDraft.id ? 'Save Changes' : 'Schedule Message'}
                    </button>
                    <button className="back-btn" onClick={() => setSchedDraft(null)}>Cancel</button>
                  </div>
                </div>
              );
            })()}

            {/* List */}
            {schedLoading ? (
              <p style={{ color: '#888' }}>Loading…</p>
            ) : scheduledMsgs.length === 0 ? (
              <p style={{ color: '#888' }}>No scheduled messages yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {scheduledMsgs.map(m => {
                  const statusStyle = {
                    pending: { bg: '#eef2ff', color: '#3c5bc4', label: 'Scheduled' },
                    sent:    { bg: '#e8f5e9', color: '#2e7d32', label: 'Sent' },
                    skipped: { bg: '#fff3e0', color: '#b26a00', label: 'Skipped' },
                    failed:  { bg: '#fdecea', color: '#c62828', label: 'Failed' },
                  }[m.status] || { bg: '#eee', color: '#555', label: m.status };
                  const isPast = Date.parse(m.sendAt) <= Date.now();
                  return (
                    <div key={m.id} style={{ border: '1.5px solid #e0e0e0', borderRadius: 10, padding: '1rem', background: m.enabled ? '#fff' : '#fafafa', opacity: m.enabled || m.status !== 'pending' ? 1 : 0.7 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#3c5bc4' }}>📅 {fmtDateTime(m.sendAt)}</span>
                            <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0.1rem 0.5rem', borderRadius: 12, background: statusStyle.bg, color: statusStyle.color }}>{statusStyle.label}</span>
                            {m.status === 'pending' && isPast && m.enabled && <span style={{ fontSize: '0.78rem', color: '#b26a00' }}>sending shortly…</span>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                            {m.label && <strong style={{ color: '#333' }}>{m.label}</strong>}
                            <span style={{ fontSize: '0.82rem', color: '#888' }}>{filterLabel(m.filter)}</span>
                          </div>
                          <p style={{ margin: '0 0 0.4rem', color: '#444', fontSize: '0.92rem', whiteSpace: 'pre-wrap' }}>{m.message}{m.includeRsvpLink && <span style={{ color: '#888' }}> + RSVP link</span>}</p>
                          {m.result && <p style={{ margin: 0, fontSize: '0.82rem', color: '#888' }}>{m.result}</p>}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                          {m.status === 'pending' && (
                            <button
                              type="button"
                              className={`deadline-toggle${m.enabled ? ' on' : ' off'}`}
                              onClick={() => toggleScheduledEnabled(m)}
                              title={m.enabled ? 'Enabled — will send' : 'Disabled — will not send'}
                            >
                              <span className="deadline-toggle-knob" />
                            </button>
                          )}
                          <div style={{ display: 'flex', gap: '0.4rem' }}>
                            {m.status === 'pending' && (
                              <button className="add-invite-btn" style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem' }} onClick={() => editSchedDraft(m)}>Edit</button>
                            )}
                            <button className="delete-btn" onClick={() => deleteScheduled(m.id)}>Delete</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── GAMES ADMIN TAB ── */}
        {activeView === 'games_admin' && (
          <div>
            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Games Map Editor</h3>

            {/* Toolbar */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem', alignItems: 'center' }}>
              {!gameAddMode ? (
                <button className="add-invite-btn" onClick={() => { setGameAddMode(true); setGameLineStep(null); }}>+ Add Game</button>
              ) : (
                <>
                  <span style={{ fontSize: '0.9rem', color: '#555', fontWeight: 500 }}>
                    {gameAddShape === 'line'
                      ? (gameLineStep ? 'Click second endpoint on map' : 'Click first endpoint on map')
                      : 'Click on map to place'}
                  </span>
                  {['rect','circle','line'].map(s => (
                    <button key={s} onClick={() => { setGameAddShape(s); setGameLineStep(null); }}
                      style={{ padding: '0.3rem 0.75rem', borderRadius: 6, border: `2px solid ${gameAddShape === s ? '#667eea' : '#ccc'}`, background: gameAddShape === s ? '#667eea' : '#fff', color: gameAddShape === s ? '#fff' : '#444', cursor: 'pointer', fontSize: '0.85rem' }}>
                      {s}
                    </button>
                  ))}
                  <button onClick={() => { setGameAddMode(false); setGameLineStep(null); }}
                    style={{ padding: '0.3rem 0.75rem', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', color: '#666' }}>
                    Cancel
                  </button>
                </>
              )}
              {gameLineStep && <span style={{ fontSize: '0.8rem', color: '#888' }}>First point set — click second point</span>}
            </div>

            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {/* Map */}
              <div style={{ flex: '0 0 auto', width: '100%', maxWidth: 460 }}>
                {gamesAdminLoading ? <p style={{ color: '#888' }}>Loading…</p> : (
                  <svg
                    ref={adminMapRef}
                    viewBox="0 0 460 560"
                    style={{ width: '100%', borderRadius: 10, cursor: gameAddMode ? 'crosshair' : 'default', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', display: 'block' }}
                    onClick={handleAdminMapClick}
                    onMouseMove={handleAdminMapMouseMove}
                    onMouseUp={handleAdminMapMouseUp}
                    onMouseLeave={handleAdminMapMouseUp}
                  >
                    <GameMapBackground />
                    {gamesAdminData.filter(g => !g.archived).map(game => {
                      const isSelected = selectedGameId === game.id;
                      const d = game.shapeData;
                      const stroke = isSelected ? 'white' : 'rgba(255,255,255,0.45)';
                      const sw = isSelected ? 3 : 1.5;
                      const gProps = {
                        key: game.id,
                        style: { cursor: gameAddMode ? 'crosshair' : 'grab' },
                        onClick: (e) => { if (!gameAddMode) { e.stopPropagation(); if (selectedGameId !== game.id) selectGame(game); } },
                        onMouseDown: (e) => handleGameDragStart(e, game),
                      };
                      if (game.shape === 'rect') return (
                        <g {...gProps}>
                          <rect x={d.x} y={d.y} width={d.w} height={d.h} fill={game.color} fillOpacity={0.85} rx={4} stroke={stroke} strokeWidth={sw} strokeDasharray={isSelected ? '4 2' : 'none'} />
                          <text x={d.x+d.w/2} y={d.y+d.h/2} textAnchor="middle" dominantBaseline="middle" fontSize={Math.min(d.w,d.h)*0.5} style={{ pointerEvents:'none' }}>{game.emoji}</text>
                          <text x={d.x+d.w/2} y={d.y+d.h+14} textAnchor="middle" fontSize={12} fill="white" stroke="rgba(0,0,0,0.6)" strokeWidth={3} paintOrder="stroke" style={{ pointerEvents:'none' }}>{game.name}</text>
                        </g>
                      );
                      if (game.shape === 'circle') return (
                        <g {...gProps}>
                          <circle cx={d.cx} cy={d.cy} r={d.r} fill={game.color} fillOpacity={0.85} stroke={stroke} strokeWidth={sw} strokeDasharray={isSelected ? '4 2' : 'none'} />
                          <text x={d.cx} y={d.cy} textAnchor="middle" dominantBaseline="middle" fontSize={d.r*0.9} style={{ pointerEvents:'none' }}>{game.emoji}</text>
                          <text x={d.cx} y={d.cy+d.r+14} textAnchor="middle" fontSize={12} fill="white" stroke="rgba(0,0,0,0.6)" strokeWidth={3} paintOrder="stroke" style={{ pointerEvents:'none' }}>{game.name}</text>
                        </g>
                      );
                      if (game.shape === 'line') {
                        const mx = (d.x1+d.x2)/2, my = (d.y1+d.y2)/2;
                        return (
                          <g {...gProps}>
                            <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} stroke="transparent" strokeWidth={24} />
                            <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} stroke={game.color} strokeWidth={isSelected?3:2.5} strokeOpacity={0.8} strokeDasharray="5 4" />
                            <circle cx={d.x1} cy={d.y1} r={8} fill={game.color} stroke={stroke} strokeWidth={sw} />
                            <circle cx={d.x2} cy={d.y2} r={8} fill={game.color} stroke={stroke} strokeWidth={sw} />
                            <text x={mx} y={my-12} textAnchor="middle" fontSize={12} style={{ pointerEvents:'none' }}>{game.emoji}</text>
                            <text x={mx} y={my+24} textAnchor="middle" fontSize={12} fill="white" stroke="rgba(0,0,0,0.6)" strokeWidth={3} paintOrder="stroke" style={{ pointerEvents:'none' }}>{game.name}</text>
                          </g>
                        );
                      }
                      return null;
                    })}
                    {/* Line first-point indicator */}
                    {gameLineStep && <circle cx={gameLineStep.x} cy={gameLineStep.y} r={6} fill="white" stroke="#667eea" strokeWidth={2} />}
                  </svg>
                )}
                <p style={{ fontSize: '0.78rem', color: '#888', margin: '0.4rem 0 0', textAlign: 'center' }}>
                  {gameAddMode ? 'Click on map to place • Cancel to exit' : 'Click game to select · Drag to reposition'}
                </p>
              </div>

              {/* Editor panel */}
              {gameDraft && (
                <div style={{ flex: '1 1 280px', minWidth: 260, background: '#f8f8f8', border: '1.5px solid #e0e0e0', borderRadius: 10, padding: '1.25rem' }}>
                  <h4 style={{ margin: '0 0 1rem', color: '#333' }}>Edit Game</h4>

                  <div className="form-group">
                    <label>Name</label>
                    <input className="search-input" value={gameDraft.name} onChange={e => setGameDraft(d => ({ ...d, name: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div ref={emojiPickerRef} className="form-group" style={{ flex: 1, position: 'relative' }}>
                      <label>Emoji</label>
                      <button
                        type="button"
                        onClick={() => setShowEmojiPicker(p => !p)}
                        style={{ width: '100%', height: 38, fontSize: '1.4rem', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        {gameDraft.emoji || '🎮'}
                      </button>
                      {showEmojiPicker && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 200, background: '#fff', border: '1.5px solid #ddd', borderRadius: 10, padding: '0.6rem', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', width: 232, maxHeight: 260, overflowY: 'auto', overflowX: 'hidden', display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2 }}>
                          {[
                            // Drinks & food
                            '🍺','🥃','🍾','🍻','🥂','🧃','🥤','🧉',
                            '🍕','🌮','🌭','🍔','🥗','🍖','🍗','🧀',
                            // Ball sports
                            '⚽','🏈','🏀','⚾','🎾','🏐','🏉','🥎',
                            '🏓','🎱','🏸','🥏','🪃','🏒','🥍','🏑',
                            // Outdoor / yard games
                            '⛳','🥅','🎣','🏹','🪁','🪃','🛹','🛷',
                            // Party & celebration
                            '🎉','🎊','🎈','🎀','🎁','🎆','🎇','✨',
                            // Trophies & competition
                            '🏆','🥇','🥈','🥉','🎖️','🏅','🎯','🎪',
                            // Classic games
                            '🎮','🕹️','🎲','🎯','🃏','🀄','🎰','🎳',
                            // Nature / yard
                            '🌽','🌿','🍀','🌸','🌻','🍂','🌲','🪵',
                            // Tools & misc
                            '🔴','🔵','🟡','🟢','🟠','🟣','⚫','⚪',
                            '🪨','🧲','🔔','📣','🚩','🏴','🎌','🏁',
                            // Animals (mascots)
                            '🦅','🦁','🐻','🦊','🐺','🦝','🐯','🦈',
                            // Weather / sky
                            '⚡','🔥','💥','❄️','🌊','🌪️','☀️','🌙',
                            // Fitness
                            '🏋️','🤸','🤼','🥊','🥋','🤺','🏇','🚴',
                            // Misc fun
                            '🤙','👊','✊','🤜','💪','🖐️','👋','🤚',
                          ].map(em => (
                            <button key={em} type="button"
                              onClick={() => { setGameDraft(d => ({ ...d, emoji: em })); setShowEmojiPicker(false); }}
                              style={{ fontSize: '1.25rem', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 4, padding: '3px', lineHeight: 1 }}
                              title={em}
                            >
                              {em}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Color</label>
                      <input type="color" value={gameDraft.color} onChange={e => setGameDraft(d => ({ ...d, color: e.target.value }))} style={{ width: '100%', height: 38, borderRadius: 6, border: '1px solid #ccc', cursor: 'pointer', padding: 2 }} />
                    </div>
                  </div>

                  {/* Shape size controls */}
                  <div className="form-group">
                    <label>Shape</label>
                    <select className="filter-select" style={{ width: '100%' }} value={gameDraft.shape}
                      onChange={e => {
                        const s = e.target.value;
                        const defaults = { rect: { x:100, y:100, w:60, h:40 }, circle: { cx:150, cy:150, r:25 }, line: { x1:100, y1:100, x2:200, y2:100 } };
                        setGameDraft(d => ({ ...d, shape: s, shapeData: defaults[s] }));
                      }}>
                      <option value="rect">Rectangle</option>
                      <option value="circle">Circle</option>
                      <option value="line">Line (facing game)</option>
                    </select>
                  </div>

                  {gameDraft.shape === 'rect' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                      {['x','y','w','h'].map(k => (
                        <div key={k} className="form-group" style={{ margin: 0 }}>
                          <label style={{ fontSize: '0.78rem' }}>{k.toUpperCase()}</label>
                          <input type="number" className="search-input" value={gameDraft.shapeData[k] ?? 0}
                            onChange={e => setGameDraft(d => ({ ...d, shapeData: { ...d.shapeData, [k]: +e.target.value } }))} />
                        </div>
                      ))}
                    </div>
                  )}
                  {gameDraft.shape === 'circle' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                      {['cx','cy','r'].map(k => (
                        <div key={k} className="form-group" style={{ margin: 0 }}>
                          <label style={{ fontSize: '0.78rem' }}>{k.toUpperCase()}</label>
                          <input type="number" className="search-input" value={gameDraft.shapeData[k] ?? 0}
                            onChange={e => setGameDraft(d => ({ ...d, shapeData: { ...d.shapeData, [k]: +e.target.value } }))} />
                        </div>
                      ))}
                    </div>
                  )}
                  {gameDraft.shape === 'line' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                      {['x1','y1','x2','y2'].map(k => (
                        <div key={k} className="form-group" style={{ margin: 0 }}>
                          <label style={{ fontSize: '0.78rem' }}>{k.toUpperCase()}</label>
                          <input type="number" className="search-input" value={gameDraft.shapeData[k] ?? 0}
                            onChange={e => setGameDraft(d => ({ ...d, shapeData: { ...d.shapeData, [k]: +e.target.value } }))} />
                        </div>
                      ))}
                    </div>
                  )}

                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.75rem 0', fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={gameDraft.indoor} onChange={e => setGameDraft(d => ({ ...d, indoor: e.target.checked }))} />
                    Played indoors
                  </label>

                  <div className="form-group">
                    <label>Intro / Flavor Text</label>
                    <textarea className="search-input" rows={3} value={gameDraft.intro} onChange={e => setGameDraft(d => ({ ...d, intro: e.target.value }))} style={{ resize: 'vertical' }} />
                  </div>

                  {/* Rules editor */}
                  <div className="form-group">
                    <label style={{ fontWeight: 600 }}>Rules Sections</label>
                    {gameDraft.rules.map((section, si) => (
                      <div key={si} style={{ marginBottom: '0.75rem', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '0.75rem' }}>
                        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                          <input className="search-input" style={{ flex: 1, fontWeight: 600 }} value={section.title}
                            placeholder="Section title"
                            onChange={e => setGameDraft(d => { const r=[...d.rules]; r[si]={...r[si],title:e.target.value}; return {...d,rules:r}; })} />
                          <button className="delete-btn" onClick={() => setGameDraft(d => ({ ...d, rules: d.rules.filter((_,i)=>i!==si) }))}>✕</button>
                        </div>
                        {section.items.map((item, ii) => (
                          <div key={ii} style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.3rem' }}>
                            <input className="search-input" style={{ flex: 1, fontSize: '0.85rem' }} value={item}
                              onChange={e => setGameDraft(d => { const r=[...d.rules]; const items=[...r[si].items]; items[ii]=e.target.value; r[si]={...r[si],items}; return {...d,rules:r}; })} />
                            <button className="delete-btn" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                              onClick={() => setGameDraft(d => { const r=[...d.rules]; r[si]={...r[si],items:r[si].items.filter((_,i)=>i!==ii)}; return {...d,rules:r}; })}>✕</button>
                          </div>
                        ))}
                        <button className="add-invite-btn" style={{ fontSize: '0.78rem', padding: '0.2rem 0.6rem', marginTop: '0.25rem' }}
                          onClick={() => setGameDraft(d => { const r=[...d.rules]; r[si]={...r[si],items:[...r[si].items,'']}; return {...d,rules:r}; })}>
                          + Item
                        </button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <input className="search-input" placeholder="New section title" value={gameNewSection.title}
                        onChange={e => setGameNewSection(s => ({ ...s, title: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter' && gameNewSection.title.trim()) { setGameDraft(d => ({ ...d, rules: [...d.rules, { title: gameNewSection.title.trim(), items: [] }] })); setGameNewSection({ title: '', item: '' }); } }} />
                      <button className="add-invite-btn" onClick={() => { if (!gameNewSection.title.trim()) return; setGameDraft(d => ({ ...d, rules: [...d.rules, { title: gameNewSection.title.trim(), items: [] }] })); setGameNewSection({ title: '', item: '' }); }}>
                        + Section
                      </button>
                    </div>

                    {/* Paste-to-fill importer */}
                    <div style={{ marginTop: '0.85rem', borderTop: '1px dashed #ddd', paddingTop: '0.75rem' }}>
                      <label style={{ fontSize: '0.82rem', color: '#666', fontWeight: 600, display: 'block', marginBottom: '0.35rem' }}>
                        ✨ Or paste rules to auto-fill
                      </label>
                      <textarea
                        value={gameRulesPaste}
                        onChange={e => setGameRulesPaste(e.target.value)}
                        rows={5}
                        placeholder={"Paste rules here. Section names on their own line, bullets beneath. e.g.\n\nTo Start\n- Set up 10 cups\n- Stand with your teammate\n\nScoring\n- In the hole: 3 pts"}
                        style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.85rem', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem' }}>
                        <button
                          type="button"
                          className="add-invite-btn"
                          disabled={!gameRulesPaste.trim()}
                          onClick={() => {
                            const { intro, sections } = parseGameRules(gameRulesPaste);
                            if (sections.length === 0 && !intro) return;
                            setGameDraft(d => ({
                              ...d,
                              rules: [...d.rules, ...sections],
                              intro: (d.intro && d.intro.trim()) ? d.intro : (intro || d.intro),
                            }));
                            setGameRulesPaste('');
                          }}
                        >
                          Parse &amp; add
                        </button>
                        <span style={{ fontSize: '0.73rem', color: '#999' }}>
                          Bullets (-, •, 1.) → items · other lines → section titles
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                    <button className="submit-btn" onClick={saveGameDraft} disabled={gameSaving} style={{ flex: 1 }}>
                      {gameSaving ? 'Saving…' : 'Save Game'}
                    </button>
                    <button
                      onClick={() => setGameArchived(gameDraft.id, true)}
                      style={{ padding: '0.5rem 1rem', borderRadius: 6, border: '1.5px solid #d6a44a', background: '#fff', color: '#b26a00', cursor: 'pointer', fontWeight: 600 }}
                      title="Hide from the public Games page, but keep it to restore later"
                    >
                      Archive
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Archived games */}
            {gamesAdminData.some(g => g.archived) && (
              <div style={{ marginTop: '2rem', borderTop: '1px solid #e0e0e0', paddingTop: '1.5rem' }}>
                <h4 style={{ margin: '0 0 0.25rem', color: '#555' }}>Archived Games</h4>
                <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                  Hidden from the public Games page. Restore one to put it back on the map, or delete it permanently.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {gamesAdminData.filter(g => g.archived).map(g => (
                    <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.85rem', background: '#fafafa', border: '1.5px solid #e8e8e8', borderRadius: 8 }}>
                      <span style={{ fontSize: '1.2rem' }}>{g.emoji}</span>
                      <span style={{ flex: 1, fontWeight: 500, color: '#555' }}>{g.name}</span>
                      <button
                        onClick={() => setGameArchived(g.id, false)}
                        style={{ padding: '0.3rem 0.75rem', borderRadius: 6, border: '1.5px solid #5b8def', background: '#fff', color: '#3c5bc4', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
                      >
                        Restore
                      </button>
                      <button className="delete-btn" onClick={() => deleteGameAdmin(g.id)}>Delete</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── GALLERY TAB ── */}
        {activeView === 'gallery' && (
          <div>
            <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Photo Gallery</h3>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>Guests upload photos from the public Photos page. Delete any photos here if needed.</p>

            {galleryLoading ? (
              <p style={{ color: '#888' }}>Loading…</p>
            ) : galleryPhotos.length === 0 ? (
              <p style={{ color: '#888' }}>No photos uploaded yet.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                {galleryPhotos.map(p => (
                  <div key={p.id} style={{ border: '1.5px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', background: '#fafafa' }}>
                    <img src={`${SERVER}${p.photoUrl}`} alt={p.title} style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
                    <div style={{ padding: '0.5rem 0.75rem' }}>
                      <p style={{ margin: '0 0 0.2rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' }}>{p.title || '(no caption)'}</p>
                      <p style={{ margin: '0 0 0.5rem', fontSize: '0.78rem', color: '#888' }}>{p.year}</p>
                      <button className="delete-btn" onClick={() => handleGalleryDelete(p.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TEAM DRAW TAB ── */}

        {/* ── RECIPIENT REVIEW MODAL ── */}
        {recipientModalOpen && (() => {
          const info = getFinalRecipientInfo();
          const search = recipientSearch.trim().toLowerCase();
          const rows = info.rows
            .filter(r => !search || r.inv.name.toLowerCase().includes(search))
            .sort((a, b) => a.inv.name.localeCompare(b.inv.name)); // stable order — no jumping when toggled
          const toggle = (r) => {
            if (!r.hasPhone && !r.included) return; // can't include someone without a phone
            setSmsOverrides(prev => ({ ...prev, [r.inv.id]: r.included ? 'exclude' : 'include' }));
          };
          return (
            <div className="settings-modal-overlay" onClick={() => setRecipientModalOpen(false)}>
              <div className="settings-modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h3 style={{ margin: 0 }}>Review Recipients</h3>
                  <button onClick={() => setRecipientModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#888', lineHeight: 1 }}>✕</button>
                </div>
                <p style={{ color: '#666', margin: '0 0 0.75rem', fontSize: '0.9rem' }}>
                  <strong style={{ color: '#2e7d32' }}>{info.includedCount}</strong> will receive this · click anyone to flip them. Overrides take precedence over the <em>{filterLabel(smsFilter)}</em> group.
                  {overrideCount > 0 && (
                    <button onClick={() => setSmsOverrides({})} style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: '#3c5bc4', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline', padding: 0 }}>
                      Reset {overrideCount} override{overrideCount !== 1 ? 's' : ''}
                    </button>
                  )}
                </p>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by name…"
                  value={recipientSearch}
                  onChange={e => setRecipientSearch(e.target.value)}
                  style={{ marginBottom: '0.75rem' }}
                />
                <div style={{ overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, flex: 1 }}>
                  {rows.length === 0 ? (
                    <p style={{ color: '#888', padding: '1rem', margin: 0 }}>No matches.</p>
                  ) : rows.map(r => {
                    const noPhone = !r.hasPhone && !r.included;
                    return (
                      <div
                        key={r.inv.id}
                        onClick={() => toggle(r)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '0.5rem 0.85rem', borderBottom: '1px solid #f3f3f3',
                          cursor: r.hasPhone || r.included ? 'pointer' : 'default',
                          background: r.included ? '#f0f9f0' : (noPhone ? '#fff' : '#fdecea'),
                          opacity: noPhone ? 0.5 : 1,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontWeight: 500, color: r.included ? '#1b5e20' : (noPhone ? '#555' : '#c62828') }}>{r.inv.name}</span>
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#999' }}>
                            {r.hasPhone ? fmtPhone(r.inv.phone) : 'no phone'}
                          </span>
                          {r.override && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', fontWeight: 600, color: '#3c5bc4', background: '#eef2ff', borderRadius: 10, padding: '0.05rem 0.45rem' }}>
                              manual
                            </span>
                          )}
                        </div>
                        <span style={{ flexShrink: 0, fontSize: '0.85rem', fontWeight: 600, color: r.included ? '#2e7d32' : (noPhone ? '#bbb' : '#c62828') }}>
                          {r.included ? '✓ Included' : (noPhone ? '—' : '✕ Excluded')}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <button className="submit-btn" style={{ marginTop: '1rem' }} onClick={() => setRecipientModalOpen(false)}>
                  Done — {info.includedCount} recipient{info.includedCount !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          );
        })()}

        {/* Hidden file input for photo uploads */}
        <input
          ref={photoFileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handlePhotoFileChange}
        />

        </div>{/* end admin-tab-content */}

        {/* ── EDIT RSVP MODAL ── */}
        {editingRSVP && (
          <div className="modal-overlay" onClick={() => setEditingRSVP(null)}>
            <div className="modal-card" onClick={e => e.stopPropagation()}>
              <h3>Edit — {editingRSVP.name}</h3>

              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="filter-select"
                  style={{ width: '100%' }}
                  value={editingRSVP.name}
                  onChange={e => setEditingRSVP({ ...editingRSVP, name: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="filter-select"
                  style={{ width: '100%' }}
                  value={editingRSVP.email}
                  placeholder="email@example.com"
                  onChange={e => setEditingRSVP({ ...editingRSVP, email: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  className="filter-select"
                  style={{ width: '100%' }}
                  value={editingRSVP.phone}
                  placeholder="(716) 555-0000"
                  onChange={e => setEditingRSVP({ ...editingRSVP, phone: fmtPhone(e.target.value) })}
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  className="filter-select"
                  value={editingRSVP.status}
                  onChange={e => setEditingRSVP({ ...editingRSVP, status: e.target.value, team: '' })}
                >
                  <option value="">— Select —</option>
                  <option value="playing">Playing</option>
                  <option value="attending">Attending (Dinner)</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </div>

              {editingRSVP.status === 'playing' && (
                <div className="form-group">
                  <label>Team</label>
                  <select
                    className="filter-select"
                    value={editingRSVP.team}
                    onChange={e => setEditingRSVP({ ...editingRSVP, team: e.target.value })}
                  >
                    <option value="">— Select Team —</option>
                    <option value="USA">Team USA</option>
                    <option value="Canada">Team Canada</option>
                  </select>
                </div>
              )}

              {(editingRSVP.status === 'playing' || editingRSVP.status === 'attending') && (
                <>
                  <div className="form-group">
                    <label>Bringing</label>
                    <input
                      type="text"
                      value={editingRSVP.bringingItem}
                      onChange={e => setEditingRSVP({ ...editingRSVP, bringingItem: e.target.value })}
                      placeholder="e.g., Drinks, Snacks"
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      rows="3"
                      value={editingRSVP.additionalNotes}
                      onChange={e => setEditingRSVP({ ...editingRSVP, additionalNotes: e.target.value })}
                      placeholder="Dietary restrictions, etc."
                    />
                  </div>
                </>
              )}

              <div className="modal-actions">
                <button className="submit-btn modal-save-btn" onClick={saveEditRSVP}>Save Changes</button>
                <button className="back-btn" onClick={() => setEditingRSVP(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {enlargedPhoto && (
          <div className="photo-modal" onClick={() => setEnlargedPhoto(null)}>
            <div className="photo-modal-content">
              <button className="close-modal" onClick={() => setEnlargedPhoto(null)}>✕</button>
              <img src={resolvePhoto(enlargedPhoto.photoUrl)} alt={enlargedPhoto.name} className="enlarged-photo" />
              <p className="photo-caption">{enlargedPhoto.name}</p>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* ── PHOTO MENU PORTAL — renders outside the overflow container ── */}
    {showPhotoMenu && (() => {
      const invite = invites.find(i => i.id === showPhotoMenu);
      if (!invite) return null;
      return ReactDOM.createPortal(
        <div
          className="photo-menu"
          style={{ position: 'fixed', top: photoMenuPos.top, left: photoMenuPos.left, zIndex: 9999, transform: 'translateX(-50%)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={() => handleEditPhoto(invite.id)} className="menu-btn">
            {invite.photoUrl ? 'Edit' : 'Add'}
          </button>
          {invite.photoUrl && (
            <>
              <button onClick={() => handleEnlargePhoto(invite)} className="menu-btn">
                Enlarge
              </button>
              <button onClick={() => handleRemovePhoto(invite.id)} className="menu-btn remove">
                Remove
              </button>
            </>
          )}
        </div>,
        document.body
      );
    })()}
    </>
  );
};

export default AdminPage;
